import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, ArrowLeft, ArrowUpDown, Calculator, Car, Check, ChevronDown, ChevronUp, Eye, FileText, GripVertical, Info, Loader2, MapPin, Plane, Plus, Save, Send, Star, Trash2, User, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { parsePrice, parseMoney, sumIsk, fmtIsk, fmtIsk0, fmtUsdApprox, fmtEurApprox, ISK_PER_USD, ISK_PER_EUR, isEuropeanEmail, hotelPriceForTotal, evalMoneyExpr, moneyToIsk, nameKey, fmtMoneyBox, fmtNum2 } from "@/lib/price";

// every money box shows the same canonical number: thousands separators + 2 decimals
const blurFmtVal = fmtMoneyBox;
import { formatGap, gapNights, ITEM_TYPES, TripView } from "./TripView";
import { HotelRateCheck } from "./HotelRateCheck";
import { VendorInvoiceControl } from "./VendorInvoiceControl";
import { CostLinesControl } from "./CostLinesControl";

const inputCls =
  "w-full border border-amber-900/40 bg-[#0d0d12] px-3 py-2.5 text-sm text-amber-100 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400";
const labelCls = "text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500";

const newItem = (day = 1) => ({ item_id: crypto.randomUUID().replace(/-/g, "").slice(0, 8), day, time: "", type: "activity", title: "", details: "" });

// Airport pickups are ordinary transport moments tagged with an auto marker — they flow into
// the schedule, the driving calendar and driver lists like any other job.
const PICKUP_MARK = "pickup-";
const isPickup = (i) => String(i.auto || "").startsWith(PICKUP_MARK);
const newPickup = (day = 1, time = "") => ({
  ...newItem(day),
  type: "transport",
  title: "Airport pickup",
  time,
  auto: `${PICKUP_MARK}${crypto.randomUUID().replace(/-/g, "").slice(0, 8)}`,
});

// timezone-safe ISO date math (no UTC shift)
const addDaysIso = (iso, n) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const tripDayCount = (a, d) => (a && d ? Math.round((new Date(`${d}T00:00:00`) - new Date(`${a}T00:00:00`)) / 86400000) + 1 : 0);

// Offline fallback for the quick list: honor "Day N" headers (and date headers when the
// arrival date is set) so lines still land on their day when the AI is unavailable.
const localScheduleLines = (lines, arrival) => {
  const months = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
  const dayHeader = /^day\s*(\d{1,2})\b[\s:;,.\-–—]*(.*)$/i;
  const bareDay = /^(\d{1,2})(?:st|nd|rd|th)?\s*$/i;
  const dateHeader = /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(\d{1,2})(?:st|nd|rd|th)?\s*[:;,\-–—]?\s*(.*)$/i;
  let day = 1;
  const out = [];
  for (const l of lines) {
    let m = l.match(dayHeader);
    if (m) {
      day = parseInt(m[1], 10) || day;
      if (m[2].trim()) out.push({ ...newItem(day), title: m[2].trim() });
      continue;
    }
    m = l.match(bareDay);
    if (m) {
      day = parseInt(m[1], 10) || day;
      continue;
    }
    m = l.match(dateHeader);
    if (m && arrival) {
      const d =
        Math.round(
          (new Date(parseInt(arrival.slice(0, 4), 10), months[m[1].toLowerCase().slice(0, 3)], parseInt(m[2], 10)) -
            new Date(`${arrival}T00:00:00`)) / 86400000
        ) + 1;
      if (d >= 1) day = d;
      if (m[3].trim()) out.push({ ...newItem(day), title: m[3].trim() });
      continue;
    }
    out.push({ ...newItem(day), title: l });
  }
  return out;
};

export default function TripBuilder({ token, trip, onDone, onCancel, adminToken = null, openCostsOnMount = false }) {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [client, setClient] = useState(trip?.client || { name: "", email: "", phone: "" });
  const [clientRegion, setClientRegion] = useState(trip?.client_region || "world");
  const [arrival, setArrival] = useState(trip?.arrival_date || "");
  const [departure, setDeparture] = useState(trip?.departure_date || "");
  const [pax, setPax] = useState(trip?.pax || "2");
  const [depositAmount, setDepositAmount] = useState(trip?.deposit_amount ? String(trip.deposit_amount) : "");
  const [depositCurrency, setDepositCurrency] = useState(trip?.deposit_currency || "usd");
  const [depositLink, setDepositLink] = useState(trip?.deposit_link || "");
  const [depositInstructions, setDepositInstructions] = useState(trip?.deposit_instructions || "");
  const [arrivalTime, setArrivalTime] = useState(trip?.arrival_time || "");
  const [departureTime, setDepartureTime] = useState(trip?.departure_time || "");
  const [location, setLocation] = useState(trip?.location || "");
  const [isPublicTour, setIsPublicTour] = useState(trip?.is_public_tour || false);
  const [ticketPrice, setTicketPrice] = useState(trip?.ticket_price || "");
  const [capacity, setCapacity] = useState(trip?.capacity ? String(trip.capacity) : "");
  const [coverImage, setCoverImage] = useState(trip?.cover_image || "");
  const [hideSpots, setHideSpots] = useState(trip?.hide_spots || false);
  const [notes, setNotes] = useState(trip?.notes || "");
  const [trimBudget, setTrimBudget] = useState("");
  const [trimBusy, setTrimBusy] = useState(false);
  const [trimResult, setTrimResult] = useState(null);
  const [costsOpen, setCostsOpen] = useState(!!openCostsOnMount);

  const runBudgetTrim = async () => {
    const budget = parseInt(String(trimBudget).replace(/\D/g, ""), 10);
    if (!budget) {
      toast.error("Enter the client's budget in ISK, e.g. 4.500.000");
      return;
    }
    setTrimBusy(true);
    setTrimResult(null);
    try {
      const { data } = await axios.post(`${API}/owner/trips/${trip.id}/budget-trim`, { budget_isk: budget }, { headers });
      setTrimResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "The advisor could not answer right now — try again");
    } finally {
      setTrimBusy(false);
    }
  };
  const [quickList, setQuickList] = useState("");
  const [reviseText, setReviseText] = useState("");
  const [revising, setRevising] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const dragRowRef = useRef(null);

  const moveRow = (from, to) => {
    if (from === to) return;
    setItems((it) => {
      const arr = [...it];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return arr;
    });
  };
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [wishState, setWishState] = useState({});
  const [guests, setGuests] = useState(trip?.guests || []);
  const [admins, setAdmins] = useState(trip?.trip_admins || []);
  const [adminEmail, setAdminEmail] = useState("");
  const [parsing, setParsing] = useState(false);
  const [aiReview, setAiReview] = useState(null);
  const [favRestaurants, setFavRestaurants] = useState([]);

  // Go-to restaurants the owner saves once and drops into any trip's lunch/dinner options
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/owner/favourites/restaurants`, { headers })
      .then((res) => setFavRestaurants(res.data.restaurants || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveFavourite = async (o) => {
    if (!o.name?.trim()) {
      toast.error("Write the restaurant name first.");
      return;
    }
    try {
      const res = await axios.post(
        `${API}/owner/favourites/restaurants`,
        { name: o.name.trim(), link: (o.link || "").trim() },
        { headers }
      );
      setFavRestaurants(res.data.restaurants || []);
      toast.success(`${o.name.trim()} saved to your favourites`);
    } catch (e) {
      if (e.response?.status === 409) toast.error("Already in your favourites");
      else toast.error("Could not save the favourite right now.");
    }
  };

  const removeFavourite = async (name) => {
    try {
      const res = await axios.delete(`${API}/owner/favourites/restaurants/${encodeURIComponent(name)}`, { headers });
      setFavRestaurants(res.data.restaurants || []);
    } catch {
      toast.error("Could not remove it right now.");
    }
  };

  const applyAiMeta = (data) => {
    const warnings = data?.warnings || [];
    const rescued = data?.rescued || [];
    setAiReview(warnings.length || rescued.length ? { warnings, rescued } : null);
    if (warnings.length)
      toast.warning(
        `${warnings.length} ${warnings.length === 1 ? "entry needs" : "entries need"} your review — see the note below the AI tools`,
        { duration: 9000 }
      );
  };
  const [hotels, setHotels] = useState(trip?.hotels?.length ? trip.hotels.map((h) => ({ ...h, price: blurFmtVal(h.price), cost: blurFmtVal(h.cost), commission: blurFmtVal(h.commission) })) : []);
  const [rentals, setRentals] = useState(trip?.rentals?.length ? trip.rentals.map((r) => ({ ...r, price: blurFmtVal(r.price), cost: blurFmtVal(r.cost), commission: blurFmtVal(r.commission) })) : []);
  // the fleet: cars guests pick from when they book a rally — first come, first served
  const [carOptions, setCarOptions] = useState(trip?.car_options?.length ? trip.car_options.map((c) => ({ ...c })) : []);
  // drivers & staff: per-person live lists of only their assigned jobs
  const [driverLinks, setDriverLinks] = useState(trip?.driver_links || []);
  const [driverEmails, setDriverEmails] = useState({});
  const [driverBusy, setDriverBusy] = useState("");
  // staff roster — driver-name suggestions right on the itinerary rows
  const [roster, setRoster] = useState([]);
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/owner/drivers`, { headers })
      .then((r) => setRoster(r.data.drivers || []))
      .catch(() => {});
  }, [token]);
  // daily rates keyed by rental row — typing a rate fills the total (rate × days)
  const [rentalRates, setRentalRates] = useState({});
  const setRental = (i, key, val) => setRentals((rs) => rs.map((r, ri) => (ri === i ? { ...r, [key]: val } : r)));
  const rentalSpan = (r) => {
    const from = parseInt(r.from_day, 10) || 1;
    return Math.max(1, (parseInt(r.to_day, 10) || from) - from + 1);
  };
  const applyRentalRate = (i) => {
    const rate = moneyToIsk(rentalRates[i] || "");
    if (rate) setRental(i, "price", fmtIsk(rate * rentalSpan(rentals[i])));
  };
  const rentalRateFor = (i) => {
    // explicit rate wins; otherwise derive it from the total when it divides cleanly
    const explicit = moneyToIsk(rentalRates[i] || "");
    if (explicit) return explicit;
    const total = moneyToIsk(rentals[i]?.price);
    const span = rentalSpan(rentals[i] || {});
    return total && total % span === 0 ? total / span : 0;
  };
  const setRentalDay = (i, key, val) => {
    setRental(i, key, val);
    const rate = rentalRateFor(i);
    if (rate) {
      const next = { ...rentals[i], [key]: val };
      setRental(i, "price", fmtIsk(rate * rentalSpan(next)));
    }
  };
  const addRental = () => setRentals((rs) => [...rs, { rental_id: "", name: "", price: "", from_day: 1, to_day: 1, notes: "" }]);
  const removeRental = (i) => setRentals((rs) => rs.filter((_, ri) => ri !== i));
  // Rally / group-trip planning — staff costs shared across clients, breakeven & sold-out math
  const [rally, setRally] = useState(() => {
    const r = {
      enabled: false,
      staff_count: 1,
      clients_booked: 1,
      staff_hotel_rate: "25.000",
      staff_dinner_rate: "8.000",
      staff_lunch_rate: "",
      staff_activity_rate: "15.000",
      staff_car: "",
      include_in_total: false,
      staff_added: false,
      price_override: "",
      ...(trip?.rally || {}),
    };
    for (const k of ["staff_hotel_rate", "staff_dinner_rate", "staff_lunch_rate", "staff_activity_rate", "staff_car", "price_override"]) r[k] = blurFmtVal(r[k]);
    return r;
  });
  const setRallyField = (k, v) => setRally((r) => ({ ...r, [k]: v }));
  const [items, setItems] = useState(trip?.items?.length ? trip.items.map((i) => ({ ...i, price: blurFmtVal(i.price), cost: blurFmtVal(i.cost), commission: blurFmtVal(i.commission) })) : [newItem(1)]);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const setClientField = (k) => (e) => setClient((c) => ({ ...c, [k]: e.target.value }));
  const setHotel = (i, k, v) => setHotels((hs) => hs.map((h, idx) => (idx === i ? { ...h, [k]: v } : h)));
  // rally staff cost math — shared by the rally panel and the costs page
  // staff costs exist only for rallies/public tours, or when the owner explicitly adds staff
  const staffVisible = !!(isPublicTour || rally.enabled || rally.staff_added || rally.include_in_total);
  const rallyStaffCosts = () => {
    const zero = { staffN: 0, awayNights: 0, totalNights: 0, tripDays: 0, staffHotel: 0, staffDinner: 0, staffLunch: 0, staffActivity: 0, staffCar: 0, staffTotal: 0 };
    if (!staffVisible) return zero;
    const staffN = parseInt(rally.staff_count, 10) || 0;
    const hotelRate = moneyToIsk(rally.staff_hotel_rate) || 0;
    const dinnerRate = moneyToIsk(rally.staff_dinner_rate) || 0;
    const lunchRate = moneyToIsk(rally.staff_lunch_rate) || 0;
    const actRate = moneyToIsk(rally.staff_activity_rate) || 0;
    const nightsOf = (h) => {
      let n = 0;
      if (h.check_in && h.check_out) n = Math.round((new Date(`${h.check_out}T00:00:00`) - new Date(`${h.check_in}T00:00:00`)) / 86400000);
      if (!n || n < 1) n = parseInt(h.nights, 10) || 1;
      return n;
    };
    const named = hotels.filter((h) => h.name.trim());
    const awayNights = named.reduce((s, h) => ((h.staff_home ?? /reykjav/i.test(`${h.name} ${h.notes || ""}`)) ? s : s + nightsOf(h)), 0);
    const totalNights = named.reduce((s, h) => s + nightsOf(h), 0);
    // lunches are covered EVERY rally day, incl. Reykjavík — breakfast comes with the hotel
    let tripDays = 0;
    if (arrival && departure) tripDays = Math.round((new Date(`${departure}T00:00:00`) - new Date(`${arrival}T00:00:00`)) / 86400000) + 1;
    if (!tripDays || tripDays < 1) tripDays = totalNights + 1;
    const staffHotel = staffN * awayNights * hotelRate;
    const staffDinner = staffN * totalNights * dinnerRate; // every night, incl. Reykjavík
    const staffLunch = staffN * tripDays * lunchRate; // every day, incl. Reykjavík
    const staffActivity = staffN * actRate; // Blue Lagoon only
    const staffCar = moneyToIsk(rally.staff_car) || 0; // one staff vehicle for the trip
    return { staffN, awayNights, totalNights, tripDays, staffHotel, staffDinner, staffLunch, staffActivity, staffCar, staffTotal: staffHotel + staffDinner + staffLunch + staffActivity + staffCar };
  };
  // staff eat what the clients eat: lunch rate defaults to the itinerary's average
  // client lunch (cost if known, else price) per person — pre-filled, editable anytime
  const lunchSuggestion = (() => {
    const lunches = items
      .filter((i) => i.type === "lunch" && !i.ops_full && !i.ops_info)
      .map((i) => moneyToIsk(i.cost) || moneyToIsk(i.price) || 0)
      .filter((n) => n > 0);
    if (!lunches.length) return 0;
    const per = Math.max(1, parseInt(pax, 10) || 2);
    return Math.round(lunches.reduce((s, n) => s + n, 0) / lunches.length / per / 100) * 100;
  })();
  useEffect(() => {
    if (lunchSuggestion > 0) {
      setRally((r) => (String(r.staff_lunch_rate || "").trim() ? r : { ...r, staff_lunch_rate: fmtNum2(lunchSuggestion) }));
    }
  }, [lunchSuggestion]);
  // dinners are priced per pair — a solo guest is billed half
  const dinnerTotalIsk = items
    .filter((i) => i.type === "dinner" && !i.ops_client_books && !i.ops_person && !i.ops_full && !i.ops_info)
    .reduce((s, i) => s + (moneyToIsk(i.price) || 0), 0);
  const ticketIsk = moneyToIsk(ticketPrice) || 0;
  const setItem = (i, k, v) => setItems((it) => it.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  // Auto-calc the third money field once two of price / cost / commission are known
  const moneyVal = (s) => {
    if (!s || !String(s).trim()) return null;
    const p = parsePrice(s);
    if (p) return { amount: p.amount, currency: p.currency };
    const pl = parseMoney(s); // plain numbers — decimals understood ("62.000,00" -> 62000)
    return pl ? { amount: pl.amount, currency: null } : null;
  };
  const fmtMoney = (amount, currency) =>
    currency
      ? `${Math.round(amount).toLocaleString("de-DE")} ${currency}`
      : Math.round(amount).toLocaleString("de-DE");
  const autoMoney = (row, setOne, i) => {
    const p = moneyVal(row.price);
    const c = moneyVal(row.cost);
    const m = moneyVal(row.commission);
    if (p && c && !m) {
      if (p.currency && c.currency && p.currency !== c.currency) return;
      setOne(i, "commission", fmtMoney(p.amount - c.amount, p.currency || c.currency));
    } else if (p && m && !c) {
      if (p.currency && m.currency && p.currency !== m.currency) return;
      setOne(i, "cost", fmtMoney(p.amount - m.amount, p.currency || m.currency));
    }
  };

  // first words too generic to identify a stay on their own
  const GENERIC_STAY_WORDS = new Set(["hotel", "the", "guesthouse", "at"]);
  // A hotel-type itinerary line (check-in/out, a plain hotel line) mirrors its stay's money:
  // one product, one price — shown and edited in both places, stored once, on the stay.
  const stayIdxForItem = (item) => {
    if (item.type !== "hotel") return -1;
    // an explicit link wins (set via the "Link to a stay" picker on the line)
    if (item.stay_id) {
      const byId = hotels.findIndex((h) => h.hotel_id && h.hotel_id === item.stay_id);
      if (byId >= 0) return byId;
    }
    const t = nameKey(`${item.title || ""} ${item.details || ""}`);
    if (!t) return -1;
    const named = hotels.map((h, hi) => ({ hi, hn: nameKey(h.name) })).filter(({ hn }) => hn);
    // a unique full-name match wins ("Check-in — Exeter Hotel" → Exeter Hotel)
    const full = named.filter(({ hn }) => t.includes(hn));
    if (full.length === 1) return full[0].hi;
    if (full.length > 1) return -1; // ambiguous — never guess with money
    // fallback: first word of the stay name, but never a generic word like "hotel"
    const word = named.filter(({ hn }) => {
      const w = hn.split(/\s+/)[0];
      return w.length > 3 && !GENERIC_STAY_WORDS.has(w) && t.includes(w);
    });
    if (word.length === 1) return word[0].hi;
    if (word.length > 1) return -1;
    // last resort: the line's significant words all appear in exactly one stay's name
    // ("Check-in: The Retreat Hotel" → "The Retreat Hotel at Blue Lagoon")
    const SKIP = new Set([...GENERIC_STAY_WORDS, "check", "in", "out"]);
    const words = t.split(/[^a-z0-9]+/).filter((w) => w.length > 3 && !SKIP.has(w));
    if (words.length) {
      const subset = named.filter(({ hn }) => words.every((w) => hn.split(/\s+/).includes(w)));
      if (subset.length === 1) return subset[0].hi;
    }
    return -1;
  };
  const stayHasLine = (si) => items.some((it) => it.type === "hotel" && stayIdxForItem(it) === si);
  // a stay that never made it into the schedule gets a linked check-in line on demand
  const addStayToItinerary = (i) => {
    const h = hotels[i];
    if (!h?.name?.trim()) return;
    const hid = h.hotel_id || crypto.randomUUID().replace(/-/g, "").slice(0, 12);
    if (!h.hotel_id) setHotel(i, "hotel_id", hid);
    let day = items[items.length - 1]?.day || 1;
    if (h.check_in && arrival) {
      const d = Math.round((new Date(`${h.check_in}T00:00:00`) - new Date(`${arrival}T00:00:00`)) / 86400000) + 1;
      if (d >= 1) day = d;
    }
    setItems((it) => [...it, { ...newItem(day), type: "hotel", time: "15:00", title: `Check-in — ${h.name}`, stay_id: hid }]);
    toast.success(`${h.name} added to the schedule — its money stays on the stay, counted once`);
  };
  const moneyItems = items.filter((x) => stayIdxForItem(x) < 0 && !x.ops_full && !x.ops_client_books && !x.ops_person && !x.ops_info); // stay-linked lines never count a copy of their own; ops lines never count at all
  // cancellation deadline chip — amber inside 7 days, red on the last day
  const cancelChip = (d, tid) => {
    if (!d) return null;
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    const n = Math.round((new Date(`${d}T00:00:00`) - t) / 86400000);
    if (Number.isNaN(n) || n < 0 || n > 7) return null;
    return (
      <span data-testid={tid} className={`shrink-0 border px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.15em] ${n <= 1 ? "border-red-500/60 bg-red-500/10 text-red-300" : "border-amber-500/50 bg-amber-500/10 text-amber-300"}`}>
        {n === 0 ? "cancel deadline today" : `cancel by ${d} — ${n}d left`}
      </span>
    );
  };
  // a rental counts once — its total price, not once per displayed day
  const totalPriceIsk = sumIsk([...hotels.map(hotelPriceForTotal), ...moneyItems.map((x) => x.price), ...rentals.map((r) => r.price)]);
  const totalCostIsk = sumIsk([...hotels, ...moneyItems, ...rentals].map((x) => x.cost));
  const totalCommIsk = sumIsk([...hotels, ...moneyItems, ...rentals].map((x) => x.commission)); // commission = the commission boxes only; empty counts as 0

  // renaming a stay retitles its schedule lines too — otherwise "Check-in — Old Name" orphans from the stay
  const stayNameRef = useRef({});
  const retitleStayLines = (idx, oldName, newName) => {
    const nn = (newName || "").trim();
    if (!nn || !oldName || oldName.trim() === nn) return;
    const h = hotels[idx];
    setItems((its) =>
      its.map((it) => {
        if (it.type !== "hotel") return it;
        const linked =
          (h?.hotel_id && it.stay_id === h.hotel_id) ||
          it.auto === `stay-${idx}-in` ||
          it.auto === `stay-${idx}-out` ||
          it.title === `Check-in — ${oldName.trim()}` ||
          it.title === `Check-out — ${oldName.trim()}`;
        if (!linked) return it;
        if (it.title.startsWith("Check-in — ")) return { ...it, title: `Check-in — ${nn}` };
        if (it.title.startsWith("Check-out — ")) return { ...it, title: `Check-out — ${nn}` };
        return it;
      })
    );
  };


  const syncStayMoments = (hotelIdx, h, arr = arrival, dep = departure) => {
    const marker = (kind) => `stay-${hotelIdx}-${kind}`;
    const toDay = (d) => {
      if (!d || !arr) return null;
      const n = Math.round((new Date(`${d}T00:00:00`) - new Date(`${arr}T00:00:00`)) / 86400000) + 1;
      const total = dep ? Math.round((new Date(`${dep}T00:00:00`) - new Date(`${arr}T00:00:00`)) / 86400000) + 1 : 999;
      return n >= 1 && n <= total ? n : null;
    };
    const mk = (kind, day, time, verb) => ({
      item_id: marker(kind),
      day,
      time,
      type: "hotel",
      title: `${verb} — ${h.name || "Hotel"}`,
      details: (h.room_mix || []).filter((x) => (x.label || "").trim()).length
        ? h.room_mix.filter((x) => (x.label || "").trim()).map((x) => `${x.qty || 1} × ${x.label.trim()}`).join(", ")
        : h.rooms
          ? `${h.rooms} × ${h.room_type || "double"}`
          : "",
      price: "",
      cost: "",
      commission: "",
      client_action: "",
      client_note: "",
      operator_notes: "",
      auto: marker(kind),
    });
    setItems((it) => {
      const rest = it.filter((x) => x.auto !== marker("in") && x.auto !== marker("out"));
      const added = [];
      const ci = toDay(h.check_in);
      const co = toDay(h.check_out);
      if (ci) added.push(mk("in", ci, "15:00", "Check-in"));
      if (co) added.push(mk("out", co, "11:00", "Check-out"));
      return [...rest, ...added].sort((a, b) => a.day - b.day || String(a.time).localeCompare(String(b.time)));
    });
    if (!arr && (h.check_in || h.check_out)) toast.error("Set the arrival date first — stay dates sink into the schedule by day");
  };

  const changeHotelDate = (i, key, v) => {
    const h = { ...hotels[i], [key]: v };
    if (h.check_out && h.check_in && h.check_out < h.check_in) h.check_out = h.check_in;
    if (h.check_in && h.check_out) {
      const nights = Math.round((new Date(`${h.check_out}T00:00:00`) - new Date(`${h.check_in}T00:00:00`)) / 86400000);
      if (nights > 0) h.nights = String(nights);
    }
    setHotels((hs) => hs.map((x, idx) => (idx === i ? h : x)));
    syncStayMoments(i, h);
  };

  const applyNights = (h) => {
    if (h.check_in && h.check_out) {
      const n = Math.round((new Date(`${h.check_out}T00:00:00`) - new Date(`${h.check_in}T00:00:00`)) / 86400000);
      if (n > 0 && String(n) !== String(h.nights || "")) return { ...h, nights: String(n) };
    }
    return h;
  };

  // Journey dates sink into the stays: one stay covers the whole journey;
  // several stays → first takes the arrival day, last takes the departure day.
  // Only empty date fields are filled — dates you set yourself are never touched.
  const fillStayDates = (arr, dep, prev = {}) => {
    if (!hotels.length) return null;
    const named = hotels.map((h, i) => ({ h, i })).filter(({ h }) => h.name.trim());
    if (!named.length) return null;
    const next = hotels.map((h) => ({ ...h }));
    let changed = false;
    // fill empty fields — and fields that still hold the previous journey date,
    // since those were auto-filled rather than chosen by the owner
    const fill = (idx, key, v, prevVal) => {
      if (v && (!next[idx][key] || (prevVal && next[idx][key] === prevVal))) {
        next[idx][key] = v;
        changed = true;
      }
    };
    if (named.length === 1) {
      fill(named[0].i, "check_in", arr, prev.prevArr);
      fill(named[0].i, "check_out", dep, prev.prevDep);
    } else {
      fill(named[0].i, "check_in", arr, prev.prevArr);
      fill(named[named.length - 1].i, "check_out", dep, prev.prevDep);
    }
    if (!changed) return null;
    next.forEach((h, i) => {
      next[i] = applyNights(h);
    });
    setHotels(next);
    toast.success("Stay dates filled from the journey dates — adjust any stay below");
    return next;
  };

  const changeArrival = (v) => {
    const prevArr = arrival;
    const dep = v && (!departure || departure < v) ? v : departure;
    setArrival(v);
    if (v && (!departure || departure < v)) setDeparture(v);
    const filled = v ? fillStayDates(v, dep, { prevArr, prevDep: departure }) : null;
    (filled || hotels).forEach((h, idx) => {
      if (h.name.trim() && (h.check_in || h.check_out)) syncStayMoments(idx, h, v, dep);
    });
  };

  const changeDeparture = (v) => {
    const prevDep = departure;
    setDeparture(v);
    if (!v) return;
    const filled = fillStayDates(arrival, v, { prevDep });
    if (arrival)
      (filled || hotels).forEach((h, idx) => {
        if (h.name.trim() && (h.check_in || h.check_out)) syncStayMoments(idx, h, arrival, v);
      });
    // trip got longer — the new days appear as their own empty itinerary sections below
    const before = tripDayCount(arrival, prevDep);
    const after = tripDayCount(arrival, v);
    if (arrival && before >= 1 && after > before) {
      toast.success(after - before === 1 ? `Day ${before + 1} is on the itinerary below` : `Days ${before + 1}–${after} are on the itinerary below`);
    }
  };

  // airport pickups: one transport moment per landing party, edited as simple date/time/who rows
  const pickupRows = items.map((item, idx) => ({ item, idx })).filter(({ item }) => isPickup(item));
  const addPickup = () => setItems((it) => [...it, newPickup(1, arrivalTime || "")]);
  const pickupDateToDay = (iso) => {
    if (!arrival || !iso) return 1;
    const n = Math.round((new Date(`${iso}T00:00:00`) - new Date(`${arrival}T00:00:00`)) / 86400000) + 1;
    return Math.max(1, Math.min(60, n));
  };

  // day-by-day grouping: one section per trip day (or per day used), rows keep their order within the day
  const dayGroups = (() => {
    const maxItemDay = items.reduce((m, i) => Math.max(m, i.day || 1), 1);
    const n = Math.max(maxItemDay, tripDayCount(arrival, departure) || 0, 1);
    const groups = [];
    for (let d = 1; d <= n; d++) groups.push({ day: d, rows: [] });
    items.forEach((item, idx) => {
      const d = Math.max(1, Math.min(item.day || 1, n));
      groups[d - 1].rows.push({ item, idx });
    });
    return groups;
  })();

  const hotelDragRef = useRef(null);
  const reorderHotels = (from, to) => {
    const next = [...hotels];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setHotels(next);
    setItems((it) => it.filter((x) => !String(x.auto || "").startsWith("stay-")));
    next.forEach((h, idx) => {
      if (h.check_in || h.check_out) syncStayMoments(idx, h);
    });
    toast.success("Stay order updated — press Save Changes to keep it");
  };

  const sortStaysByDate = () => {
    const dated = hotels.filter((h) => h.check_in);
    const undated = hotels.filter((h) => !h.check_in);
    dated.sort((a, b) => (a.check_in < b.check_in ? -1 : a.check_in > b.check_in ? 1 : 0));
    const next = [...dated, ...undated];
    setHotels(next);
    setItems((it) => it.filter((x) => !String(x.auto || "").startsWith("stay-")));
    next.forEach((h, idx) => {
      if (h.check_in || h.check_out) syncStayMoments(idx, h);
    });
    toast.success("Stays sorted by check-in — press Save Changes to keep it");
  };

  const buildPayload = () => ({
    client,
    client_region: clientRegion,
    arrival_date: arrival,
    departure_date: departure,
    pax,
    arrival_time: arrivalTime,
    departure_time: departureTime,
    location,
    deposit_amount: parseFloat(depositAmount) || 0,
    deposit_currency: depositCurrency,
    deposit_link: depositLink,
    deposit_instructions: depositInstructions,
    is_public_tour: isPublicTour,
    ticket_price: ticketPrice,
    capacity: parseInt(capacity, 10) || 0,
    cover_image: coverImage,
    hide_spots: hideSpots,
    rally: { ...rally, staff_count: parseInt(rally.staff_count, 10) || 0, clients_booked: parseInt(rally.clients_booked, 10) || 0 },
    hotels: hotels.filter((h) => h.name.trim()),
    rentals: rentals.filter((r) => r.name.trim()).map((r) => ({ ...r, from_day: parseInt(r.from_day, 10) || 1, to_day: parseInt(r.to_day, 10) || parseInt(r.from_day, 10) || 1 })),
    car_options: carOptions.filter((c) => (c.label || "").trim()).map((c) => ({ ...c, qty: parseInt(c.qty, 10) || 1 })),
    items: items
      .filter((i) => i.title.trim())
      .map((i) => {
        const options = (i.options || []).filter((o) => o.name?.trim());
        return {
          ...i,
          options,
          picked_option: options.some((o) => o.option_id === i.picked_option) ? i.picked_option : "",
        };
      }),
  });

  const openPreview = () => setPreviewOpen(true);

  const moveItem = (itemId, targetDay, beforeId) => {
    setItems((it) => {
      const moving = it.find((x) => x.item_id === itemId);
      if (!moving) return it;
      const rest = it.filter((x) => x.item_id !== itemId);
      const updated = { ...moving, day: targetDay };
      if (beforeId) {
        const idx = rest.findIndex((x) => x.item_id === beforeId);
        if (idx !== -1) return [...rest.slice(0, idx), updated, ...rest.slice(idx)];
      }
      const lastIdx = rest.map((x) => x.day).lastIndexOf(targetDay);
      if (lastIdx === -1) return [...rest, updated];
      return [...rest.slice(0, lastIdx + 1), updated, ...rest.slice(lastIdx + 1)];
    });
  };

  // drivers & staff: create/resend/revoke their personal live lists
  const driverAction = async (kind, name, email = "", linkToken = "") => {
    setDriverBusy(`${kind}:${name}`);
    try {
      if (kind === "create") {
        const res = await axios.post(`${API}/owner/trips/${trip.id}/driver-links`, { name, email }, { headers });
        const l = res.data.link;
        setDriverLinks((ls) => [...ls.filter((x) => x.name.trim().toLowerCase() !== name.trim().toLowerCase()), { name: l.name, email: l.email, token: l.token }]);
        toast.success(`List sent to ${name}`);
      } else if (kind === "resend") {
        await axios.post(`${API}/owner/trips/${trip.id}/driver-links/${linkToken}/resend`, {}, { headers });
        toast.success(`List sent again to ${name}`);
      } else if (kind === "revoke") {
        await axios.delete(`${API}/owner/trips/${trip.id}/driver-links/${linkToken}`, { headers });
        setDriverLinks((ls) => ls.filter((x) => x.token !== linkToken));
        toast.success(`${name}'s access revoked`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not do that right now");
    } finally {
      setDriverBusy("");
    }
  };

  // open a driver/staff's uploaded invoice (owner-only endpoint — fetch with auth, open as blob)
  const viewDriverInvoice = async (invoiceId) => {
    try {
      const res = await axios.get(`${API}/owner/trips/${trip.id}/driver-invoice/${invoiceId}`, { headers, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Could not open the invoice");
    }
  };

  const save = async (stayHere = false) => {    if (!client.name.trim() || !client.email.trim()) {
      toast.error("Client name and email are required.");
      return false;
    }
    setSaving(true);
    try {
      const res = adminToken
        ? await axios.put(`${API}/trip-admin/${adminToken}`, buildPayload())
        : trip?.id
          ? await axios.put(`${API}/owner/trips/${trip.id}`, buildPayload(), { headers })
          : await axios.post(`${API}/owner/trips`, buildPayload(), { headers });
      toast.success(trip?.id ? "Itinerary updated" : "Itinerary created — nothing is confirmed until you mark it");
      if (!stayHere) onDone(res.data);
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save the itinerary.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const [status, setStatus] = useState(trip?.status || "draft");
  const [approving, setApproving] = useState(false);

  const approveMyself = async () => {
    if (!trip?.id) return;
    setApproving(true);
    try {
      const ok = await save(true); // persist current edits first so approval covers them
      if (!ok) return;
      await axios.post(`${API}/owner/trips/${trip.id}/approve`, {}, { headers });
      setStatus("pending_confirmation");
      toast.success("Itinerary approved — driving jobs are now on the calendar awaiting assignment");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not approve right now");
    } finally {
      setApproving(false);
    }
  };

  // calculator money fields: "25.000*4" computes on blur, then the price/cost/commission auto-fill runs on the result
  const calcField = (row, setOne, i, k) => {
    const raw = String(row[k] || "").trim();
    // "20%" in the commission box = 20% of the price (owner's decision: of the price, not on cost)
    const pct = k === "commission" ? raw.match(/^(\d+(?:[.,]\d+)?)\s*%$/) : null;
    if (pct) {
      const p = moneyVal(row.price);
      if (p) {
        const fmt = fmtMoney((p.amount * parseFloat(pct[1].replace(",", "."))) / 100, p.currency);
        setOne(i, k, fmt);
        setOne(i, "exprs", { ...(row.exprs || {}), [k]: `${raw} of ${row.price}` });
        autoMoney({ ...row, [k]: fmt }, setOne, i); // cost fills in as price − commission when empty
        return;
      }
    }
    const v = evalMoneyExpr(raw);
    const fmt = blurFmtVal(v || row[k]); // canonical whole number
    if (fmt !== (row[k] || "")) {
      setOne(i, k, fmt);
      if (v) setOne(i, "exprs", { ...(row.exprs || {}), [k]: raw }); // remember the math — hover shows it
    }
    autoMoney({ ...row, [k]: fmt }, setOne, i);
  };

  // hover a money box that was calculated — see the math behind it
  const exprHint = (row, k) => (row?.exprs?.[k] ? ` — your math: ${row.exprs[k]} → ${row[k]}` : "");

  // hover a cost box with an itemised split — see the lines without opening the popup
  const costSplitHint = (row) => (row?.cost_lines?.length ? ` — split: ${row.cost_lines.map((l) => `${l.label || "?"} ${l.amount}`).join(" + ")}` : "");

  // per-person rate × guests = the price-box total (plain numbers stay plain, currencies keep their code)
  const paxN = parseInt(pax, 10) || 0;
  const ppTotal = (pp, n) => {
    const p = parseMoney(evalMoneyExpr(pp) ?? pp);
    if (!p || !p.amount || !n) return "";
    const coded = /\b(ISK|EUR|USD)\b/i.test(String(pp));
    return fmtMoney(p.amount * n, coded ? p.currency : null);
  };
  const setPerPerson = (i, v) =>
    setItems((it) =>
      it.map((x, xi) => {
        if (xi !== i) return x;
        const next = { ...x, per_person: v };
        const total = ppTotal(v, paxN);
        if (total) next.price = total;
        return next;
      })
    );
  // guest count changed → every per-person row re-prices
  const repricePerPerson = (n) =>
    setItems((it) =>
      it.map((x) => {
        if (!x.per_person) return x;
        const total = ppTotal(x.per_person, n);
        return total ? { ...x, price: total } : x;
      })
    );

  return (
    <div data-testid="trip-builder">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          data-testid="builder-back-button"
          onClick={onCancel}
          className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-neutral-400 transition-colors hover:text-amber-200"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All itineraries
        </button>
        <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
          {trip?.id ? "Editing itinerary" : "New client itinerary"}
        </p>
      </div>

      {trip?.status === "changes_requested" && (
        <div data-testid="builder-changes-banner" className="mt-6 border border-orange-400/40 bg-orange-500/10 p-4 text-sm text-orange-200">
          Your client requested adjustments — revise below, then resend for a fresh review.
        </div>
      )}

      {trip?.client_wishes?.length > 0 && (
        <div data-testid="builder-wishes-banner" className="mt-6 border border-amber-400/40 bg-amber-500/10 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
            Client wishes — straight from their page
          </p>
          <div className="mt-3 space-y-3">
            {trip.client_wishes.map((w, i) => {
              const handled = wishState[i]?.handled ?? w.handled;
              const reply = wishState[i]?.reply ?? w.reply;
              return (
                <div key={i} data-testid={`wish-row-${i}`} className={`border border-amber-500/20 bg-[#0c0c10] p-3 ${handled ? "opacity-55" : ""}`}>
                  <p className="font-serif text-base italic text-amber-100/90">
                    &ldquo;{w.text}&rdquo;
                    {w.from && (
                      <span data-testid={`wish-from-${i}`} className="ml-2 font-sans text-[10px] not-italic uppercase tracking-[0.2em] text-amber-300/70">
                        — guest: {w.from}
                      </span>
                    )}
                  </p>
                  {reply && (
                    <p className="mt-2 border-l-2 border-emerald-500/50 pl-3 text-xs text-emerald-200/90">
                      You replied: {reply}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {handled ? (
                      <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-emerald-300">Applied</span>
                    ) : (
                      <button
                        data-testid={`wish-apply-${i}`}
                        disabled={revising}
                        onClick={async () => {
                          setSnapshot({ hotels, items });
                          setRevising(true);
                          try {
                            const res = await axios.post(
                              `${API}/owner/trips/revise`,
                              {
                                instruction: w.text,
                                arrival_date: arrival,
                                departure_date: departure,
                                hotels: hotels.filter((h) => h.name.trim()),
                                items: items.filter((x) => x.title.trim()),
                              },
                              { headers }
                            );
                            setHotels(res.data.hotels);
                            setItems(res.data.items);
                            await axios.post(`${API}/owner/trips/${trip.id}/wish-handled`, { index: i }, { headers });
                            setWishState((s) => ({ ...s, [i]: { ...s[i], handled: true } }));
                            toast.success("Wish applied — review the schedule below, undo if needed");
                          } catch (e) {
                            const detail = e.response?.data?.detail;
                            toast.error(typeof detail === "string" ? detail : "Could not apply the wish.");
                          } finally {
                            setRevising(false);
                          }
                        }}
                        className="border border-amber-500/60 bg-amber-500/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-50"
                      >
                        {revising ? "Applying..." : "Apply with AI"}
                      </button>
                    )}
                    <button
                      data-testid={`wish-reply-toggle-${i}`}
                      onClick={() => {
                        setReplyOpen(replyOpen === i ? null : i);
                        setReplyText("");
                      }}
                      className="border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                    >
                      Reply
                    </button>
                  </div>
                  {replyOpen === i && (
                    <div className="mt-2 flex gap-2">
                      <input
                        data-testid={`wish-reply-input-${i}`}
                        placeholder="Your reply — lands on their page and in their inbox"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        className={`${inputCls} flex-1`}
                      />
                      <button
                        data-testid={`wish-reply-send-${i}`}
                        onClick={async () => {
                          if (!replyText.trim()) return;
                          try {
                            await axios.post(
                              `${API}/owner/trips/${trip.id}/wish-reply`,
                              { index: i, reply: replyText },
                              { headers }
                            );
                            setWishState((s) => ({ ...s, [i]: { ...s[i], reply: replyText } }));
                            setReplyText("");
                            setReplyOpen(null);
                            toast.success("Reply sent — it's on your client's page now");
                          } catch {
                            toast.error("Could not send the reply.");
                          }
                        }}
                        className="shrink-0 bg-amber-500 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-300"
                      >
                        Send
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-8 space-y-8">
        <div className="border border-amber-500/15 bg-[#14141a] p-6">
          <p className={labelCls}>The Client</p>
          {trip?.policy_ack && (
            <p data-testid="policy-ack-note" className="mt-3 border border-emerald-500/30 bg-emerald-500/5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-emerald-300">
              Cancellation policies confirmed by {trip.policy_ack.name} · {new Date(trip.policy_ack.at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            </p>
          )}
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <input data-testid="trip-client-name" placeholder="Full name *" value={client.name} onChange={setClientField("name")} className={inputCls} />
            <input
              data-testid="trip-client-email"
              type="email"
              placeholder="Email *"
              value={client.email}
              onChange={setClientField("email")}
              onBlur={(e) => {
                if (isEuropeanEmail(e.target.value) && clientRegion !== "eu") {
                  setClientRegion("eu");
                  toast.info("European email detected — conversion set to EUR. You can change it below.");
                }
              }}
              className={inputCls}
            />
            <label className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-600">Client based in</span>
              <select
                data-testid="trip-client-region"
                value={clientRegion}
                onChange={(e) => setClientRegion(e.target.value)}
                className={inputCls}
                title="European clients see the conversion in EUR; everyone else sees USD"
              >
                <option value="world">Rest of world — USD conversion</option>
                <option value="eu">Europe — EUR conversion</option>
              </select>
            </label>
            <input data-testid="trip-client-phone" placeholder="Phone (optional)" value={client.phone} onChange={setClientField("phone")} className={inputCls} />
          </div>
        </div>

        <div className="border border-amber-500/15 bg-[#14141a] p-6">
          <p className={labelCls}>The Journey</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Arrival</span>
              <input data-testid="trip-arrival" type="date" value={arrival} onChange={(e) => changeArrival(e.target.value)} className={`${inputCls} mt-1.5`} />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Arrival time</span>
              <input data-testid="trip-arrival-time" type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)} className={`${inputCls} mt-1.5`} />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Departure</span>
              <input data-testid="trip-departure" type="date" value={departure} min={arrival || undefined} onChange={(e) => changeDeparture(e.target.value)} className={`${inputCls} mt-1.5`} />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Departure time</span>
              <input data-testid="trip-departure-time" type="time" value={departureTime} onChange={(e) => setDepartureTime(e.target.value)} className={`${inputCls} mt-1.5`} />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Guests (pax)</span>
              <input data-testid="trip-pax" type="number" min="1" value={pax} onChange={(e) => { setPax(e.target.value); repricePerPerson(parseInt(e.target.value, 10) || 0); }} className={`${inputCls} mt-1.5`} />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Deposit amount (0 = none)</span>
              <input data-testid="trip-deposit-amount" type="number" min="0" step="0.01" placeholder="e.g. 500" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} className={`${inputCls} mt-1.5`} />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Deposit currency</span>
              <select data-testid="trip-deposit-currency" value={depositCurrency} onChange={(e) => setDepositCurrency(e.target.value)} className={`${inputCls} mt-1.5`}>
                <option value="usd">USD</option>
                <option value="eur">EUR</option>
                <option value="isk">ISK</option>
                <option value="gbp">GBP</option>
              </select>
            </label>
          </div>
          <div className="mt-5 border-t border-neutral-800/60 pt-4" data-testid="pickups-section">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Airport pickups — the party lands at different times</p>
              <button
                data-testid="pickup-add"
                onClick={addPickup}
                className="flex shrink-0 items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
              >
                <Plus className="h-3 w-3" /> Add a pickup
              </button>
            </div>
            {pickupRows.length === 0 ? (
              <p className="mt-2 text-[11px] text-neutral-600">Everyone lands together? The arrival time above covers it. Add a row per extra landing — each becomes a transport moment on the schedule you can price and assign to a driver.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {pickupRows.map(({ item, idx }, pi) => (
                  <div key={item.item_id} data-testid={`pickup-row-${pi}`} className="flex flex-wrap items-center gap-2 border border-neutral-800/80 bg-black/20 p-3">
                    <Plane className="h-4 w-4 shrink-0 text-amber-400/60" />
                    <input
                      data-testid={`pickup-date-${pi}`}
                      type="date"
                      value={arrival ? addDaysIso(arrival, item.day - 1) : ""}
                      min={arrival || undefined}
                      max={departure || undefined}
                      onChange={(e) => setItem(idx, "day", pickupDateToDay(e.target.value))}
                      className={`${inputCls} !w-40`}
                    />
                    <input data-testid={`pickup-time-${pi}`} type="time" value={item.time} onChange={(e) => setItem(idx, "time", e.target.value)} className={`${inputCls} !w-28`} />
                    <input
                      data-testid={`pickup-flight-${pi}`}
                      placeholder="Flight — BA800"
                      title="Flight number — the driver's list links it to the live arrival status"
                      value={item.flight || ""}
                      onChange={(e) => setItem(idx, "flight", e.target.value.toUpperCase())}
                      className={`${inputCls} !w-32`}
                    />
                    <input
                      data-testid={`pickup-note-${pi}`}
                      placeholder="Who's landing — flight no. (e.g. Anna & James, BA800)"
                      value={item.details}
                      onChange={(e) => setItem(idx, "details", e.target.value)}
                      className={`${inputCls} min-w-48 flex-1`}
                    />
                    <button
                      data-testid={`pickup-remove-${pi}`}
                      onClick={() => setItems((it) => it.filter((x) => x.item_id !== item.item_id))}
                      className="text-neutral-600 transition-colors hover:text-red-400"
                      aria-label="Remove pickup"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Payment link (optional)</span>
              <input data-testid="trip-deposit-link" placeholder="PayPal.me, SaltPay, any payment URL" value={depositLink} onChange={(e) => setDepositLink(e.target.value)} className={`${inputCls} mt-1.5`} />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Payment instructions (optional)</span>
              <input data-testid="trip-deposit-instructions" placeholder="e.g. Bank transfer to KT 010165…, ref: client name" value={depositInstructions} onChange={(e) => setDepositInstructions(e.target.value)} className={`${inputCls} mt-1.5`} />
            </label>
          </div>
        </div>

        {!adminToken && (
        <div className="border border-dashed border-amber-500/40 bg-amber-500/[0.04] p-6">
          <p className={labelCls}>Fast Notes — write it rough, AI structures it</p>
          <p className="mt-2 text-xs leading-relaxed text-neutral-500">
            Hotels, tours, dinners, drivers, surprises — dump it all in any order. Day 1 is the arrival day.
          </p>
          <textarea
            data-testid="trip-notes-input"
            rows={4}
            placeholder="e.g. Arriving Friday 14:20, EDITION 2 nights then ION. Day 2 golden circle in the morning, dinner at Dill 19:00. Lava cave party Saturday night, glacier truck convoy Sunday 10:00, farewell dinner at the villa 20:00..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={`${inputCls} mt-3 resize-none`}
          />
          <button
            data-testid="generate-from-notes-button"
            onClick={async () => {
              if (!notes.trim()) {
                toast.error("Write your notes first.");
                return;
              }
              setParsing(true);
              setAiReview(null);
              try {
                const res = await axios.post(
                  `${API}/owner/trips/parse`,
                  { notes, arrival_date: arrival, departure_date: departure },
                  { headers }
                );
                const parsed = res.data;
                if (parsed.hotels?.length) {
                  const base = hotels.filter((h) => h.name.trim()).length;
                  setHotels((hs) => [...hs.filter((h) => h.name.trim()), ...parsed.hotels]);
                  parsed.hotels.forEach((h, k) => {
                    if (h.check_in || h.check_out) syncStayMoments(base + k, h);
                  });
                }
                if (parsed.items?.length)
                  setItems((it) =>
                    [...it.filter((i) => i.title.trim()), ...parsed.items].sort(
                      (a, b) => a.day - b.day || (a.time || "").localeCompare(b.time || "")
                    )
                  );
                applyAiMeta(parsed);
                toast.success(
                  `Composed ${parsed.items?.length || 0} moments and ${parsed.hotels?.length || 0} stays — review below`
                );
              } catch (e) {
                const detail = e.response?.data?.detail;
                toast.error(typeof detail === "string" ? detail : "Could not compose from these notes.");
              } finally {
                setParsing(false);
              }
            }}
            disabled={parsing}
            className="mt-4 flex items-center gap-2 bg-amber-500 px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300 disabled:opacity-60"
          >
            {parsing && <Loader2 className="h-4 w-4 animate-spin" />}
            {parsing ? "Composing your itinerary..." : "Generate Itinerary"}
          </button>

          <div className="mt-5 border-t border-amber-500/15 pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">
              Or list it line by line — AI schedules the days
            </p>
            <textarea
              data-testid="trip-quicklist-input"
              rows={5}
              placeholder={"One per line, dates or day numbers welcome:\nDec 4: check in EDITION, dinner at Dill 19:00\nDec 5: golden circle, lava cave party 21:00\nDec 6: glacier truck convoy, dinner at the villa"}
              value={quickList}
              onChange={(e) => setQuickList(e.target.value)}
              className={`${inputCls} mt-3 resize-none`}
            />
            <button
              data-testid="quicklist-add-button"
              onClick={async () => {
                const lines = quickList.split("\n").map((l) => l.trim()).filter(Boolean);
                if (!lines.length) {
                  toast.error("List at least one item first.");
                  return;
                }
                setParsing(true);
                setAiReview(null);
                try {
                  const res = await axios.post(
                    `${API}/owner/trips/parse`,
                    { notes: lines.join("\n"), arrival_date: arrival, departure_date: departure },
                    { headers }
                  );
                  const parsed = res.data;
                  if (parsed.hotels?.length) {
                    const base = hotels.filter((h) => h.name.trim()).length;
                    setHotels((hs) => [...hs.filter((h) => h.name.trim()), ...parsed.hotels]);
                    parsed.hotels.forEach((h, k) => {
                      if (h.check_in || h.check_out) syncStayMoments(base + k, h);
                    });
                  }
                  if (parsed.items?.length)
                    setItems((it) =>
                      [...it.filter((i) => i.title.trim()), ...parsed.items].sort(
                        (a, b) => a.day - b.day || (a.time || "").localeCompare(b.time || "")
                      )
                    );
                  setQuickList("");
                  applyAiMeta(parsed);
                  toast.success(
                    `Scheduled ${parsed.items?.length || 0} moments across your days — review below`
                  );
                } catch (e) {
                  setItems((it) => [
                    ...it.filter((i) => i.title.trim()),
                    ...localScheduleLines(lines, arrival),
                  ]);
                  setQuickList("");
                  toast.error("AI unavailable — kept your day/date markers, adjust anything below");
                } finally {
                  setParsing(false);
                }
              }}
              disabled={parsing}
              className="mt-3 flex items-center gap-2 border border-amber-500/50 px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-60"
            >
              {parsing && <Loader2 className="h-4 w-4 animate-spin" />}
              {parsing ? "Scheduling..." : "Add to Schedule"}
            </button>
            <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-neutral-600">
              Days, times and types land in place — edit anything below, insert a moment anywhere with the + on its row, move rows with the arrows, or add at the end with &ldquo;Add moment&rdquo;
            </p>
          </div>

          <div className="mt-5 border-t border-amber-500/15 pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">
              Revise with AI — changes what&apos;s already below
            </p>
            <textarea
              data-testid="trip-revise-input"
              rows={2}
              placeholder='e.g. "Move the lava cave party to day 3 and swap Dill for Moss" — untouched items stay exactly as they are'
              value={reviseText}
              onChange={(e) => setReviseText(e.target.value)}
              className={`${inputCls} mt-3 resize-none`}
            />
            <button
              data-testid="revise-with-ai-button"
              onClick={async () => {
                if (!reviseText.trim()) {
                  toast.error("Write a revision instruction first.");
                  return;
                }
                setSnapshot({ hotels, items });
                setRevising(true);
                setAiReview(null);
                try {
                  const res = await axios.post(
                    `${API}/owner/trips/revise`,
                    {
                      instruction: reviseText,
                      arrival_date: arrival,
                      departure_date: departure,
                      hotels: hotels.filter((h) => h.name.trim()),
                      items: items.filter((i) => i.title.trim()),
                    },
                    { headers }
                  );
                  setHotels(res.data.hotels);
                  setItems(res.data.items);
                  setReviseText("");
                  applyAiMeta(res.data);
                  toast.success("Schedule revised — review the changes below");
                } catch (e) {
                  const detail = e.response?.data?.detail;
                  toast.error(typeof detail === "string" ? detail : "Could not apply the revision.");
                } finally {
                  setRevising(false);
                }
              }}
              disabled={revising}
              className="mt-3 flex items-center gap-2 border border-amber-500/50 px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-60"
            >
              {revising && <Loader2 className="h-4 w-4 animate-spin" />}
              {revising ? "Revising..." : "Revise Schedule"}
            </button>
          </div>

          {aiReview && (
            <div data-testid="ai-review-panel" className="mt-5 border border-amber-500/40 bg-amber-500/[0.06] p-4">
              <div className="flex items-center justify-between">
                <p className={labelCls}>Double-check the AI&apos;s work</p>
                <button
                  data-testid="ai-review-dismiss"
                  onClick={() => setAiReview(null)}
                  aria-label="Dismiss"
                  className="text-neutral-500 transition-colors hover:text-amber-200"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {aiReview.warnings.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {aiReview.warnings.map((w, i) => (
                    <li key={`w${i}`} data-testid={`ai-warning-${i}`} className="flex items-start gap-2 text-xs leading-relaxed text-amber-200">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              )}
              {aiReview.rescued.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {aiReview.rescued.map((r, i) => (
                    <li key={`r${i}`} data-testid={`ai-rescue-${i}`} className="flex items-start gap-2 text-xs leading-relaxed text-neutral-400">
                      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500/70" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        )}

        <div className="border border-amber-500/15 bg-[#14141a] p-6">
          <div className="flex items-center justify-between">
            <p className={labelCls}>Hotels & Stays</p>
            <div className="flex items-center gap-2">
              <button
                data-testid="trip-sort-stays"
                onClick={sortStaysByDate}
                className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
              >
                <ArrowUpDown className="h-3 w-3" /> Sort by date
              </button>
              <button
                data-testid="trip-add-hotel"
                onClick={() =>
                  setHotels((hs) => [
                    ...hs,
                    hs.length === 0 && arrival
                      ? applyNights({ name: "", notes: "", check_in: arrival, check_out: departure || "" })
                      : { name: "", notes: "" },
                  ])
                }
                className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
              >
                <Plus className="h-3 w-3" /> Add stay
              </button>
            </div>
          </div>
          {hotels.length === 0 && <p className="mt-3 text-sm text-neutral-600">No stays added yet.</p>}
          {hotels.length > 0 &&
            (() => {
              const anyDated = hotels.some((h) => h.check_in && h.check_out);
              if (!anyDated)
                return (
                  <p className="mt-3 text-xs text-neutral-600">
                    Set In/Out dates on your stays and I'll flag any night with no hotel right here.
                  </p>
                );
              const gaps = gapNights({ arrival_date: arrival, departure_date: departure, hotels });
              if (!gaps.length)
                return (
                  <p data-testid="stay-gap-ok" className="mt-3 text-xs text-emerald-300/80">
                    Every night of the journey is covered by a stay.
                  </p>
                );
              return (
                <div data-testid="stay-gap-warning-builder" className="mt-3 border border-dashed border-red-500/50 bg-red-950/10 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-red-300/90">
                    Unbooked nights — no stay assigned
                  </p>
                  <p className="mt-1.5 text-xs leading-relaxed tracking-wide text-red-200/80">
                    {gaps.map((iso) => formatGap({ arrival_date: arrival }, iso)).join("  ·  ")}
                  </p>
                </div>
              );
            })()}
          <div className="mt-4 space-y-3">
            {hotels.map((h, i) => (
              <div
                key={i}
                data-testid={`trip-hotel-row-${i}`}
                draggable
                onDragStart={() => {
                  hotelDragRef.current = i;
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (hotelDragRef.current !== null && hotelDragRef.current !== i) reorderHotels(hotelDragRef.current, i);
                  hotelDragRef.current = null;
                }}
                className="flex flex-wrap items-center gap-3"
              >
                <div className="flex shrink-0 flex-col">
                  <button
                    data-testid={`trip-hotel-up-${i}`}
                    disabled={i === 0}
                    onClick={() => reorderHotels(i, i - 1)}
                    className="text-neutral-500 transition-colors hover:text-amber-300 disabled:opacity-25"
                    aria-label="Move stay up"
                    title="Move stay up"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`trip-hotel-down-${i}`}
                    disabled={i === hotels.length - 1}
                    onClick={() => reorderHotels(i, i + 1)}
                    className="text-neutral-500 transition-colors hover:text-amber-300 disabled:opacity-25"
                    aria-label="Move stay down"
                    title="Move stay down"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>
                <GripVertical data-testid={`trip-hotel-grip-${i}`} className="h-4 w-4 shrink-0 cursor-grab text-neutral-700 transition-colors hover:text-amber-300" />
                <input
                  data-testid={`trip-hotel-name-${i}`}
                  placeholder="Hotel name"
                  value={h.name}
                  onFocus={() => { stayNameRef.current[i] = h.name; }}
                  onBlur={() => retitleStayLines(i, stayNameRef.current[i], h.name)}
                  onChange={(e) => {
                    setHotel(i, "name", e.target.value);
                    if (h.check_in || h.check_out) syncStayMoments(i, { ...h, name: e.target.value });
                  }}
                  className={`${inputCls} flex-1 min-w-40`}
                />
                {h.name.trim() && !stayHasLine(i) && (
                  <button
                    data-testid={`trip-hotel-addline-${i}`}
                    onClick={() => addStayToItinerary(i)}
                    title="This stay isn't in the schedule yet — add a check-in line that mirrors this stay's money"
                    className="flex shrink-0 items-center gap-1 border border-amber-500/40 px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-[0.15em] text-amber-200/90 transition-colors hover:bg-amber-500 hover:text-black"
                  >
                    <Plus className="h-3 w-3" /> To schedule
                  </button>
                )}
                <label className="flex items-center gap-1.5" title="Check-in — sinks a check-in moment into the schedule">
                  <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-600">In</span>
                  <input data-testid={`trip-hotel-checkin-${i}`} type="date" value={h.check_in || ""} min={arrival || undefined} max={departure || undefined} onChange={(e) => changeHotelDate(i, "check_in", e.target.value)} className={`${inputCls} !w-36`} />
                </label>
                <label className="flex items-center gap-1.5" title="Check-out — sinks a check-out moment into the schedule">
                  <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-600">Out</span>
                  <input data-testid={`trip-hotel-checkout-${i}`} type="date" value={h.check_out || ""} min={h.check_in || arrival || undefined} max={departure || undefined} onChange={(e) => changeHotelDate(i, "check_out", e.target.value)} className={`${inputCls} !w-36`} />
                </label>
                <label className="flex items-center gap-1.5" title="Free-cancellation deadline for this stay — the client acknowledges it, and rallies remind you if tickets are unsold as it approaches">
                  <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-600">Cancel by</span>
                  <input data-testid={`trip-hotel-cancelby-${i}`} type="date" value={h.cancel_by || ""} onChange={(e) => setHotel(i, "cancel_by", e.target.value)} className={`${inputCls} !w-36`} />
                </label>
                {cancelChip(h.cancel_by, `trip-hotel-cancelchip-${i}`)}
                <input data-testid={`trip-hotel-notes-${i}`} placeholder="Notes" value={h.notes} onChange={(e) => setHotel(i, "notes", e.target.value)} className={`${inputCls} flex-1 min-w-32`} />
                <input data-testid={`trip-hotel-nights-${i}`} type="number" min="1" placeholder="Nights" title="Nights at this hotel" value={h.nights || ""} onChange={(e) => setHotel(i, "nights", e.target.value)} className={`${inputCls} !w-24`} />
                <input data-testid={`trip-hotel-rooms-${i}`} type="number" min="1" placeholder="Rooms" title="Number of rooms" value={h.rooms || ""} onChange={(e) => setHotel(i, "rooms", e.target.value)} className={`${inputCls} !w-24`} />
                <select
                  data-testid={`trip-hotel-roomtype-${i}`}
                  value={h.room_type || "double"}
                  onChange={(e) => setHotel(i, "room_type", e.target.value)}
                  className="border border-amber-900/40 bg-[#0d0d12] px-2 py-2.5 text-sm text-amber-100 outline-none focus:border-amber-400"
                >
                  <option value="double">Double</option>
                  <option value="twin">Twin</option>
                </select>
                <input data-testid={`trip-hotel-price-${i}`} placeholder="Price" title={`Client-facing price — type math like 25.000*4 and it calculates when you leave the field${exprHint(h, "price")}`} value={h.price || ""} onChange={(e) => setHotel(i, "price", e.target.value)} onBlur={() => calcField(h, setHotel, i, "price")} className={`${inputCls} !w-28`} />
                <label className="flex items-center gap-1.5" title="Divide the total across the nights — the check-out day is free (24h rule)">
                  <input
                    data-testid={`trip-hotel-split-${i}`}
                    type="checkbox"
                    checked={!!h.split_price}
                    onChange={(e) => setHotel(i, "split_price", e.target.checked)}
                    className="h-3.5 w-3.5 accent-amber-500"
                  />
                  <span className="text-[9px] uppercase tracking-[0.18em] text-neutral-500">÷ nights</span>
                </label>
                <input data-testid={`trip-hotel-cost-${i}`} placeholder="Cost" title={`Your cost — private — math works here too${exprHint(h, "cost")}${costSplitHint(h)}`} value={h.cost || ""} onChange={(e) => setHotel(i, "cost", e.target.value)} onBlur={() => calcField(h, setHotel, i, "cost")} className={`${inputCls} !w-24 border-dashed`} />
                <CostLinesControl
                  row={h}
                  testid={`trip-hotel-costlines-${i}`}
                  onApply={(lines, totalStr) => {
                    setHotel(i, "cost_lines", lines);
                    if (totalStr) {
                      setHotel(i, "cost", totalStr);
                      autoMoney({ ...h, cost: totalStr }, setHotel, i);
                    }
                  }}
                />
                <input data-testid={`trip-hotel-commission-${i}`} placeholder="Comm." title={`Your commission — auto-fills from price − cost, or type 20% to take that share of the price${exprHint(h, "commission")}`} value={h.commission || ""} onChange={(e) => setHotel(i, "commission", e.target.value)} onBlur={() => calcField(h, setHotel, i, "commission")} className={`${inputCls} !w-24 border-dashed`} />
                {token && <HotelRateCheck token={token} hotel={h} arrival={arrival} departure={departure} pax={pax} index={i} />}
                {token && trip?.id && (
                  <>
                    <label className="flex items-center gap-1.5" title="I do this job myself — no vendor invoice needed, no VAT credit">
                      <input
                        data-testid={`trip-hotel-selfdone-${i}`}
                        type="checkbox"
                        checked={!!h.self_done}
                        onChange={(e) => setHotel(i, "self_done", e.target.checked)}
                        className="h-3.5 w-3.5 accent-amber-500"
                      />
                      <span className="text-[9px] uppercase tracking-[0.18em] text-neutral-500">Myself</span>
                    </label>
                    <label className="flex items-center gap-1.5" title="There is no invoice for this one — stop reminding me about it">
                      <input
                        data-testid={`trip-hotel-noinvoice-${i}`}
                        type="checkbox"
                        checked={!!h.no_invoice}
                        onChange={(e) => setHotel(i, "no_invoice", e.target.checked)}
                        className="h-3.5 w-3.5 accent-amber-500"
                      />
                      <span className="text-[9px] uppercase tracking-[0.18em] text-neutral-500">No inv.</span>
                    </label>
                    {rally.enabled && (
                      <label className="flex items-center gap-1.5" title="Rally staffing: staff sleep at home this night (Reykjavík) — no staff hotel or dinner needed">
                        <input
                          data-testid={`trip-hotel-staffhome-${i}`}
                          type="checkbox"
                          checked={h.staff_home ?? /reykjav/i.test(`${h.name} ${h.notes || ""}`)}
                          onChange={(e) => setHotel(i, "staff_home", e.target.checked)}
                          className="h-3.5 w-3.5 accent-amber-500"
                        />
                        <span className="text-[9px] uppercase tracking-[0.18em] text-neutral-500">Staff home</span>
                      </label>
                    )}
                    <VendorInvoiceControl
                      base={`${API}/owner/trips/${trip.id}`}
                      headers={headers}
                      kind="hotel"
                      rowKey={h.hotel_id || String(i)}
                      row={h}
                      onField={(f, v) => setHotel(i, f, f === "vendor_vat" ? Number(v) : v)}
                      onRows={(items2, hotels2) => {
                        const src = (hotels2 || [])[i];
                        if (src)
                          setHotels((xs) =>
                            xs.map((x, xi) =>
                              xi === i
                                ? { ...x, hotel_id: src.hotel_id || x.hotel_id, vendor_invoice_path: src.vendor_invoice_path || "", vendor_invoice_type: src.vendor_invoice_type || "", vendor_invoice_at: src.vendor_invoice_at || "" }
                                : x
                            )
                          );
                      }}
                      testid={`trip-hotel-inv-${i}`}
                    />
                  </>
                )}
                <button
                  data-testid={`trip-hotel-remove-${i}`}
                  onClick={() => {
                    const remaining = hotels.filter((_, idx) => idx !== i);
                    setHotels(remaining);
                    setItems((it) => it.filter((x) => !String(x.auto || "").startsWith("stay-")));
                    remaining.forEach((h2, idx2) => {
                      if (h2.check_in || h2.check_out) syncStayMoments(idx2, h2);
                    });
                  }}
                  className="text-neutral-600 transition-colors hover:text-red-400"
                  aria-label="Remove stay"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                <div className="w-full space-y-1.5 border-l border-amber-500/15 pl-4">
                  {(h.room_options || []).map((o, oi) => (
                    <div key={o.option_id || oi} className="flex items-center gap-2">
                      <input
                        data-testid={`room-option-label-${i}-${oi}`}
                        placeholder="Room option, e.g. Deluxe with lagoon view"
                        value={o.label}
                        onChange={(e) => setHotel(i, "room_options", h.room_options.map((x, xi) => (xi === oi ? { ...x, label: e.target.value } : x)))}
                        className={`${inputCls} min-w-40 flex-1`}
                      />
                      <input
                        data-testid={`room-option-price-${i}-${oi}`}
                        placeholder="Price"
                        value={o.price}
                        onChange={(e) => setHotel(i, "room_options", h.room_options.map((x, xi) => (xi === oi ? { ...x, price: e.target.value } : x)))}
                        onBlur={() => {
                          const v = evalMoneyExpr(o.price);
                          if (v) setHotel(i, "room_options", h.room_options.map((x, xi) => (xi === oi ? { ...x, price: v } : x)));
                        }}
                        title="Type math like 25.000*4 and it calculates when you leave the field"
                        className={`${inputCls} !w-28`}
                      />
                      <button
                        data-testid={`room-option-remove-${i}-${oi}`}
                        onClick={() => setHotel(i, "room_options", h.room_options.filter((_, xi) => xi !== oi))}
                        className="text-neutral-600 transition-colors hover:text-red-400"
                        aria-label="Remove room option"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    data-testid={`room-option-add-${i}`}
                    onClick={() => setHotel(i, "room_options", [...(h.room_options || []), { option_id: "", label: "", price: "" }])}
                    className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-500 transition-colors hover:text-amber-200"
                  >
                    <Plus className="h-3 w-3" /> Room option with its own price — the client ticks their favourite
                  </button>
                  {h.picked_option && (
                    <p data-testid={`room-picked-${i}`} className="text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-300">
                      Client picked: {(h.room_options || []).find((o) => o.option_id === h.picked_option)?.label || h.picked_option}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-600">Rooms we hold:</span>
                    {(h.room_mix || []).map((rm, ri) => (
                      <span key={ri} className="flex items-center gap-1">
                        <input
                          data-testid={`room-mix-qty-${i}-${ri}`}
                          type="number"
                          min="1"
                          title="How many rooms of this type"
                          value={rm.qty}
                          onChange={(e) => setHotel(i, "room_mix", h.room_mix.map((x, xi) => (xi === ri ? { ...x, qty: e.target.value } : x)))}
                          className={`${inputCls} !w-14 !px-1.5 text-center`}
                        />
                        <span className="text-[10px] text-neutral-600">×</span>
                        <input
                          data-testid={`room-mix-label-${i}-${ri}`}
                          placeholder="Double, Suite…"
                          value={rm.label}
                          onChange={(e) => setHotel(i, "room_mix", h.room_mix.map((x, xi) => (xi === ri ? { ...x, label: e.target.value } : x)))}
                          className={`${inputCls} !w-36`}
                        />
                        <button
                          data-testid={`room-mix-remove-${i}-${ri}`}
                          onClick={() => setHotel(i, "room_mix", h.room_mix.filter((_, xi) => xi !== ri))}
                          className="text-neutral-600 transition-colors hover:text-red-400"
                          aria-label="Remove room type"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <button
                      data-testid={`room-mix-add-${i}`}
                      onClick={() => setHotel(i, "room_mix", [...(h.room_mix || []), { label: "", qty: "1" }])}
                      title="How many of each room type we have at this hotel — shows on the check-in line"
                      className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-500 transition-colors hover:text-amber-200"
                    >
                      <Plus className="h-3 w-3" /> Room type
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-amber-500/15 bg-[#14141a] p-6">
          <div className="flex items-center justify-between">
            <p className={labelCls}>Rentals — vehicles priced per day</p>
            <button
              data-testid="trip-add-rental"
              onClick={addRental}
              className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              <Plus className="h-3 w-3" /> Add rental
            </button>
          </div>
          <p className="mt-1 text-[11px] text-neutral-500">
            Enter the vehicle once with its total price and the days it covers — every covered day then shows the vehicle with its daily share on the
            itinerary, Agent Sheet and PDF. The total counts once. Delivery fees stay as separate itinerary moments.
          </p>
          <div className="mt-4 space-y-3">
            {rentals.length === 0 && <p className="text-[11px] text-neutral-600">No rentals on this trip.</p>}
            {rentals.map((r, i) => {
              const fromD = parseInt(r.from_day, 10) || 1;
              const span = Math.max(1, (parseInt(r.to_day, 10) || fromD) - fromD + 1);
              const perDay = moneyToIsk(r.price) ? fmtIsk(Math.round(moneyToIsk(r.price) / span)) : "";
              return (
                <div key={r.rental_id || i} data-testid={`trip-rental-${i}`} className="flex flex-wrap items-center gap-2 border border-neutral-800/80 bg-black/20 p-3">
                  <Car className="h-4 w-4 shrink-0 text-amber-400/60" />
                  <input data-testid={`trip-rental-name-${i}`} placeholder="Vehicle, e.g. Land Rover Defender" value={r.name} onChange={(e) => setRental(i, "name", e.target.value)} className={`${inputCls} min-w-52 flex-1`} />
                  {(() => {
                    const total = moneyToIsk(r.price);
                    const derived = total && total % span === 0 ? fmtNum2(total / span) : "";
                    return (
                      <label className="flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] text-amber-300/70">
                        <input
                          data-testid={`trip-rental-rate-${i}`}
                          placeholder="Per day"
                          title="Price per day — the total fills in automatically (rate × days)"
                          value={rentalRates[i] ?? derived}
                          onChange={(e) => setRentalRates((m) => ({ ...m, [i]: e.target.value }))}
                          onBlur={() => applyRentalRate(i)}
                          className={`${inputCls} !w-24 border-amber-500/30`}
                        />
                        /day
                      </label>
                    );
                  })()}
                  <input
                    data-testid={`trip-rental-price-${i}`}
                    placeholder="Total price"
                    title={`Total for the whole rental — math works here too (e.g. 50.200*9)${exprHint(r, "price")}`}
                    value={r.price || ""}
                    onChange={(e) => setRental(i, "price", e.target.value)}
                    onBlur={() => {
                      const v = evalMoneyExpr(r.price);
                      if (v) {
                        setRental(i, "price", v);
                        setRental(i, "exprs", { ...(r.exprs || {}), price: r.price }); // remember the math — hover shows it
                      }
                    }}
                    className={`${inputCls} !w-28`}
                  />
                  <input
                    data-testid={`trip-rental-cost-${i}`}
                    placeholder="Cost"
                    title={`Your cost — private, never shown to clients — math works here too${exprHint(r, "cost")}${costSplitHint(r)}`}
                    value={r.cost || ""}
                    onChange={(e) => setRental(i, "cost", e.target.value)}
                    onBlur={() => calcField(r, setRental, i, "cost")}
                    className={`${inputCls} !w-24 border-dashed`}
                  />
                  <CostLinesControl
                    row={r}
                    testid={`trip-rental-costlines-${i}`}
                    onApply={(lines, totalStr) => {
                      setRental(i, "cost_lines", lines);
                      if (totalStr) {
                        setRental(i, "cost", totalStr);
                        autoMoney({ ...r, cost: totalStr }, setRental, i);
                      }
                    }}
                  />
                  <input
                    data-testid={`trip-rental-commission-${i}`}
                    placeholder="Comm."
                    title={`Your commission — auto-fills from price − cost, or type 20% to take that share of the price${exprHint(r, "commission")}`}
                    value={r.commission || ""}
                    onChange={(e) => setRental(i, "commission", e.target.value)}
                    onBlur={() => calcField(r, setRental, i, "commission")}
                    className={`${inputCls} !w-24 border-dashed`}
                  />
                  <label className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    Day
                    <input data-testid={`trip-rental-from-${i}`} type="number" min="1" title="First day the vehicle is with the client" value={r.from_day} onChange={(e) => setRentalDay(i, "from_day", e.target.value)} className={`${inputCls} !w-16`} />
                  </label>
                  <label className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    to
                    <input data-testid={`trip-rental-to-${i}`} type="number" min="1" title="Last day — the client keeps it through this day" value={r.to_day} onChange={(e) => setRentalDay(i, "to_day", e.target.value)} className={`${inputCls} !w-16`} />
                  </label>
                  <label className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-neutral-500" title="Free-cancellation deadline for this rental — the client acknowledges it, and rallies remind you if tickets are unsold as it approaches">
                    cancel
                    <input data-testid={`trip-rental-cancelby-${i}`} type="date" value={r.cancel_by || ""} onChange={(e) => setRental(i, "cancel_by", e.target.value)} className={`${inputCls} !w-36`} />
                  </label>
                  {cancelChip(r.cancel_by, `trip-rental-cancelchip-${i}`)}
                  <input data-testid={`trip-rental-notes-${i}`} placeholder="Notes — private" value={r.notes || ""} onChange={(e) => setRental(i, "notes", e.target.value)} className={`${inputCls} min-w-28 flex-1`} />
                  {token && trip?.id && (
                    <>
                      <label className="flex items-center gap-1.5" title="I provide the vehicle myself — no vendor invoice needed, no VAT credit">
                        <input
                          data-testid={`trip-rental-selfdone-${i}`}
                          type="checkbox"
                          checked={!!r.self_done}
                          onChange={(e) => setRental(i, "self_done", e.target.checked)}
                          className="h-3.5 w-3.5 accent-amber-500"
                        />
                        <span className="text-[9px] uppercase tracking-[0.18em] text-neutral-500">Myself</span>
                      </label>
                      <label className="flex items-center gap-1.5" title="There is no invoice for this one — stop reminding me about it">
                        <input
                          data-testid={`trip-rental-noinvoice-${i}`}
                          type="checkbox"
                          checked={!!r.no_invoice}
                          onChange={(e) => setRental(i, "no_invoice", e.target.checked)}
                          className="h-3.5 w-3.5 accent-amber-500"
                        />
                        <span className="text-[9px] uppercase tracking-[0.18em] text-neutral-500">No inv.</span>
                      </label>
                      <VendorInvoiceControl
                        base={`${API}/owner/trips/${trip.id}`}
                        headers={headers}
                        kind="rental"
                        rowKey={r.rental_id || String(i)}
                        row={r}
                        onField={(f, v) => setRental(i, f, f === "vendor_vat" ? Number(v) : v)}
                        onRows={(items2, hotels2, rentals2) => {
                          const src = (rentals2 || []).find((x) => x.rental_id && x.rental_id === r.rental_id);
                          if (src)
                            setRentals((xs) =>
                              xs.map((x, xi) =>
                                xi === i
                                  ? { ...x, rental_id: src.rental_id || x.rental_id, vendor_invoice_path: src.vendor_invoice_path || "", vendor_invoice_type: src.vendor_invoice_type || "", vendor_invoice_at: src.vendor_invoice_at || "" }
                                  : x
                              )
                            );
                        }}
                        testid={`trip-rental-inv-${i}`}
                      />
                    </>
                  )}
                  {perDay && (
                    <span data-testid={`trip-rental-perday-${i}`} className="text-[10px] uppercase tracking-[0.15em] text-amber-300/80">
                      {span} days · {perDay} / day
                    </span>
                  )}
                  <button data-testid={`trip-rental-remove-${i}`} onClick={() => removeRental(i)} className="text-neutral-600 transition-colors hover:text-red-400" aria-label="Remove rental">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {(isPublicTour || carOptions.length > 0) && (
          <div className="border border-amber-500/15 bg-[#14141a] p-6">
            <div className="flex items-center justify-between">
              <p className={labelCls}>The fleet — cars guests pick, first come first served</p>
              <button
                data-testid="car-option-add"
                onClick={() => setCarOptions((cs) => [...cs, { option_id: "", label: "", qty: "1" }])}
                className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
              >
                <Plus className="h-3 w-3" /> Add car
              </button>
            </div>
            <p className="mt-1 text-[11px] text-neutral-500">
              How many of each car we have for this rally. Guests pick theirs when they book — once the last one of a type is taken, it&apos;s gone for the rest.
            </p>
            <div className="mt-4 space-y-2">
              {carOptions.length === 0 && <p className="text-[11px] text-neutral-600">No cars listed — guests book without picking one.</p>}
              {carOptions.map((c, i) => {
                const taken = c.option_id ? (trip?.guests || []).filter((g) => g.registered && g.car_choice === c.option_id).length : 0;
                return (
                  <div key={c.option_id || i} data-testid={`car-option-${i}`} className="flex flex-wrap items-center gap-2 border border-neutral-800/80 bg-black/20 p-3">
                    <Car className="h-4 w-4 shrink-0 text-amber-400/60" />
                    <input
                      data-testid={`car-option-qty-${i}`}
                      type="number"
                      min="1"
                      title="How many of this car we have"
                      value={c.qty}
                      onChange={(e) => setCarOptions((cs) => cs.map((x, xi) => (xi === i ? { ...x, qty: e.target.value } : x)))}
                      className={`${inputCls} !w-16 text-center`}
                    />
                    <span className="text-[10px] text-neutral-600">×</span>
                    <input
                      data-testid={`car-option-label-${i}`}
                      placeholder="Land Rover Defender"
                      value={c.label}
                      onChange={(e) => setCarOptions((cs) => cs.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))}
                      className={`${inputCls} min-w-52 flex-1`}
                    />
                    {c.option_id && (
                      <span data-testid={`car-option-taken-${i}`} className={`text-[10px] uppercase tracking-[0.15em] ${taken >= (parseInt(c.qty, 10) || 1) ? "text-red-400" : "text-neutral-500"}`}>
                        {taken}/{parseInt(c.qty, 10) || 1} taken
                      </span>
                    )}
                    <button data-testid={`car-option-remove-${i}`} onClick={() => setCarOptions((cs) => cs.filter((_, xi) => xi !== i))} className="text-neutral-600 transition-colors hover:text-red-400" aria-label="Remove car">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {trip?.id && (() => {
          const assigneeNames = [...new Set(items.map((i) => (i.assignee || "").trim()).filter(Boolean))];
          const rowNames = [...new Set([...assigneeNames, ...driverLinks.map((l) => (l.name || "").trim()).filter(Boolean)])].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
          const jobCount = (name) => items.filter((i) => (i.assignee || "").trim().toLowerCase() === name.toLowerCase()).length;
          return (
            <div className="border border-amber-500/15 bg-[#14141a] p-6" data-testid="drivers-section">
              <p className={labelCls}>Drivers & staff — their own live list</p>
              <p className="mt-1 text-[11px] text-neutral-500">
                Put a name on any job — the driver box sits right on each itinerary row (roster names suggested), or use the calendar, sheet or ops chain. Then send that person their list: they see only their own jobs with dates and times, plus the client&apos;s contact — never prices. Change a time on one of their jobs and they get an email. Their page shows who to invoice and lets them upload it straight to you. Set their phone/email on the roster (Calendar → pencil) and the client sees it next to their name.
              </p>
              <div className="mt-4 space-y-2">
                {rowNames.length === 0 && (
                  <p className="text-[11px] text-neutral-600">Nobody assigned yet — name a driver on a job and they appear here.</p>
                )}
                {rowNames.map((name, ri) => {
                  const link = driverLinks.find((l) => (l.name || "").trim().toLowerCase() === name.toLowerCase());
                  const count = jobCount(name);
                  const busy = driverBusy !== "";
                  const personInvoices = (trip?.driver_invoices || []).filter((inv) => (inv.name || "").trim().toLowerCase() === name.toLowerCase());
                  return (
                    <div key={name} data-testid={`driver-row-${ri}`} className="flex flex-wrap items-center gap-2 border border-neutral-800/80 bg-black/20 p-3">
                      <User className="h-4 w-4 shrink-0 text-amber-400/60" />
                      <span className="font-serif text-sm text-neutral-100">{name}</span>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">{count} job{count === 1 ? "" : "s"}</span>
                      <span className="flex-1" />
                      {personInvoices.map((inv) => (
                        <button
                          key={inv.invoice_id}
                          data-testid={`driver-invoice-view-${inv.invoice_id}`}
                          onClick={() => viewDriverInvoice(inv.invoice_id)}
                          className="flex items-center gap-1.5 border border-emerald-500/40 bg-emerald-950/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-emerald-200 transition-colors hover:border-emerald-400"
                        >
                          <FileText className="h-3 w-3" /> invoice — {inv.filename}
                        </button>
                      ))}
                      {link ? (
                        <>
                          <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-300/80">list sent to {link.email}</span>
                          <button data-testid={`driver-resend-${ri}`} disabled={busy} onClick={() => driverAction("resend", link.name, "", link.token)} className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200 disabled:opacity-40">
                            <Send className="h-3 w-3" /> Resend
                          </button>
                          <button data-testid={`driver-revoke-${ri}`} disabled={busy} onClick={() => driverAction("revoke", link.name, "", link.token)} className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-400 transition-colors hover:border-red-500/60 hover:text-red-300 disabled:opacity-40">
                            Revoke
                          </button>
                        </>
                      ) : (
                        <>
                          <input
                            data-testid={`driver-email-${ri}`}
                            type="email"
                            placeholder="their email"
                            value={driverEmails[name] || ""}
                            onChange={(e) => setDriverEmails((m) => ({ ...m, [name]: e.target.value }))}
                            className={`${inputCls} !w-52`}
                          />
                          <button
                            data-testid={`driver-send-${ri}`}
                            disabled={busy || !(driverEmails[name] || "").includes("@")}
                            onClick={() => driverAction("create", name, (driverEmails[name] || "").trim())}
                            className="flex items-center gap-1.5 border border-amber-500/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-40"
                          >
                            <Send className="h-3 w-3" /> Send their list
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        <div className="border border-amber-500/15 bg-[#14141a] p-6">
          <div className="flex items-center justify-between">
            <p className={labelCls}>Rally — staffing, capacity & breakeven</p>
            <button
              data-testid="rally-toggle"
              onClick={() => setRallyField("enabled", !rally.enabled)}
              aria-pressed={rally.enabled}
              className={`border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                rally.enabled ? "border-amber-400 bg-amber-500/10 text-amber-200" : "border-neutral-700 text-neutral-400 hover:border-amber-400 hover:text-amber-200"
              }`}
            >
              {rally.enabled ? "Rally — on" : "Not a rally"}
            </button>
          </div>
          {rally.enabled && (() => {
            const staffN = parseInt(rally.staff_count, 10) || 0;
            const booked = parseInt(rally.clients_booked, 10) || 0;
            const maxC = parseInt(capacity, 10) || 0;
            const hotelRate = moneyToIsk(rally.staff_hotel_rate) || 0;
            const dinnerRate = moneyToIsk(rally.staff_dinner_rate) || 0;
            const lunchRate = moneyToIsk(rally.staff_lunch_rate) || 0;
            const actRate = moneyToIsk(rally.staff_activity_rate) || 0;
            // away nights: stays where staff can't sleep at home (auto: anything not Reykjavík — override per stay above)
            const awayNights = hotels
              .filter((h) => h.name.trim())
              .reduce((s, h) => {
                const home = h.staff_home ?? /reykjav/i.test(`${h.name} ${h.notes || ""}`);
                if (home) return s;
                let n = 0;
                if (h.check_in && h.check_out) n = Math.round((new Date(`${h.check_out}T00:00:00`) - new Date(`${h.check_in}T00:00:00`)) / 86400000);
                if (!n || n < 1) n = parseInt(h.nights, 10) || 1;
                return s + n;
              }, 0);
            const staffHotel = staffN * awayNights * hotelRate;
            // dinners are covered EVERY night of the rally — also in Reykjavík; only hotel nights are away-only
            const totalNights = hotels
              .filter((h) => h.name.trim())
              .reduce((s, h) => {
                let n = 0;
                if (h.check_in && h.check_out) n = Math.round((new Date(`${h.check_out}T00:00:00`) - new Date(`${h.check_in}T00:00:00`)) / 86400000);
                if (!n || n < 1) n = parseInt(h.nights, 10) || 1;
                return s + n;
              }, 0);
            const staffDinner = staffN * totalNights * dinnerRate;
            // lunches every rally day, incl. Reykjavík — breakfast comes with the hotel
            let tripDays = 0;
            if (arrival && departure) tripDays = Math.round((new Date(`${departure}T00:00:00`) - new Date(`${arrival}T00:00:00`)) / 86400000) + 1;
            if (!tripDays || tripDays < 1) tripDays = totalNights + 1;
            const staffLunch = staffN * tripDays * lunchRate;
            const staffActivity = staffN * actRate; // Blue Lagoon — the only paid staff activity
            const staffCar = moneyToIsk(rally.staff_car) || 0; // one staff vehicle for the trip
            const staffTotal = staffHotel + staffDinner + staffLunch + staffActivity + staffCar;
            const perClientIncome = sumIsk([...hotels.map(hotelPriceForTotal), ...moneyItems.map((x) => x.price), ...rentals.map((r) => r.price)]);
            const perClientCost = sumIsk([...hotels, ...moneyItems, ...rentals].map((x) => x.cost));
            const margin = perClientIncome - perClientCost;
            const breakeven = margin > 0 ? Math.max(1, Math.ceil(staffTotal / margin)) : null;
            const totalIncome = perClientIncome * booked;
            const totalCost = perClientCost * booked + staffTotal;
            const totalComm = totalIncome - totalCost;
            const soldOutProfit = maxC > 0 ? margin * maxC - staffTotal : null;
            const rowCls = "flex items-center justify-between py-1 text-[11px] uppercase tracking-[0.15em]";
            const numCls = "font-luxe text-sm tracking-[0.08em] text-amber-100";
            return (
              <div className="mt-4 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    Staff
                    <input data-testid="rally-staff-count" type="number" min="1" max="8" title="How many staff the rally needs — normally 1 to 4" value={rally.staff_count} onChange={(e) => setRallyField("staff_count", e.target.value)} className={`${inputCls} !w-16`} />
                  </label>
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    Clients booked
                    <input data-testid="rally-clients-booked" type="number" min="0" value={rally.clients_booked} onChange={(e) => setRallyField("clients_booked", e.target.value)} className={`${inputCls} !w-16`} />
                  </label>
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    Max capacity
                    <input data-testid="rally-max-capacity" type="number" min="0" title="The most people that can come on this rally" value={capacity} onChange={(e) => setCapacity(e.target.value)} className={`${inputCls} !w-16`} />
                  </label>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    Staff hotel / night
                    <input data-testid="rally-hotel-rate" title="Per staff member per night away from Reykjavík — math works here" value={rally.staff_hotel_rate} onChange={(e) => setRallyField("staff_hotel_rate", e.target.value)} onBlur={() => { const v = evalMoneyExpr(rally.staff_hotel_rate); setRallyField("staff_hotel_rate", v || blurFmtVal(rally.staff_hotel_rate)); }} className={`${inputCls} !w-24`} />
                  </label>
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    Staff dinner / night
                    <input data-testid="rally-dinner-rate" title="Per staff member per night — every night, incl. Reykjavík. Math works here" value={rally.staff_dinner_rate} onChange={(e) => setRallyField("staff_dinner_rate", e.target.value)} onBlur={() => { const v = evalMoneyExpr(rally.staff_dinner_rate); setRallyField("staff_dinner_rate", v || blurFmtVal(rally.staff_dinner_rate)); }} className={`${inputCls} !w-24`} />
                  </label>
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    Staff lunch / day
                    <input data-testid="rally-lunch-rate" title="Per staff member per rally day — every day, incl. Reykjavík. Math works here" value={rally.staff_lunch_rate} onChange={(e) => setRallyField("staff_lunch_rate", e.target.value)} onBlur={() => { const v = evalMoneyExpr(rally.staff_lunch_rate); setRallyField("staff_lunch_rate", v || blurFmtVal(rally.staff_lunch_rate)); }} className={`${inputCls} !w-24`} />
                  </label>
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    Blue Lagoon / staff
                    <input data-testid="rally-activity-rate" title="The only activity we pay for staff — per staff member, once per rally" value={rally.staff_activity_rate} onChange={(e) => setRallyField("staff_activity_rate", e.target.value)} onBlur={() => { const v = evalMoneyExpr(rally.staff_activity_rate); setRallyField("staff_activity_rate", v || blurFmtVal(rally.staff_activity_rate)); }} className={`${inputCls} !w-24`} />
                    <label className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.15em] text-neutral-500">
                      Staff car
                      <input
                        data-testid="rally-staff-car"
                        title="One staff vehicle for the whole trip — total ISK"
                        placeholder="total ISK"
                        value={rally.staff_car}
                        onChange={(e) => setRallyField("staff_car", e.target.value)}
                        onBlur={() => { const v = evalMoneyExpr(rally.staff_car); setRallyField("staff_car", v || blurFmtVal(rally.staff_car)); }}
                        className={`${inputCls} !w-24`}
                      />
                    </label>
                  </label>
                </div>
                <p className="text-[11px] text-neutral-500">
                  Staff sleep at home on Reykjavík nights — no hotel for them there — but dinners are covered every night and lunches every day of the rally, Reykjavík included (breakfast comes with the hotel). Away nights: <span data-testid="rally-away-nights" className="text-amber-200">{awayNights}</span> (auto from the stays above; tick “Staff home” on a stay to change it). Activities are never paid for staff — except the Blue Lagoon.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="border border-neutral-800/80 bg-black/20 p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Staff cost — shared by all clients</p>
                    <div className="mt-2 divide-y divide-neutral-800/60">
                      <div className={rowCls}><span className="text-neutral-500">Hotels ({staffN} × {awayNights}n)</span><span className={numCls} data-testid="rally-staff-hotel">{fmtIsk0(staffHotel)}</span></div>
                      <div className={rowCls}><span className="text-neutral-500">Dinners ({staffN} × {totalNights}n — every night, incl. Reykjavík)</span><span className={numCls} data-testid="rally-staff-dinner">{fmtIsk0(staffDinner)}</span></div>
                      <div className={rowCls}><span className="text-neutral-500">Lunches ({staffN} × {tripDays}d — every day)</span><span className={numCls} data-testid="rally-staff-lunch">{fmtIsk0(staffLunch)}</span></div>
                      <div className={rowCls}><span className="text-neutral-500">Staff car (whole trip)</span><span className={numCls} data-testid="rally-staff-car-total">{fmtIsk0(staffCar)}</span></div>
                      <div className={rowCls}><span className="text-neutral-500">Blue Lagoon ({staffN} × once)</span><span className={numCls} data-testid="rally-staff-activity">{fmtIsk0(staffActivity)}</span></div>
                      <div className={rowCls}><span className="text-amber-300/80">Staff total</span><span className={numCls} data-testid="rally-staff-total">{fmtIsk0(staffTotal)}</span></div>
                    </div>
                  </div>
                  <div className="border border-neutral-800/80 bg-black/20 p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Per client (from the itinerary above)</p>
                    <div className="mt-2 divide-y divide-neutral-800/60">
                      <div className={rowCls}><span className="text-neutral-500">Income</span><span className={numCls} data-testid="rally-per-client-income">{fmtIsk0(perClientIncome)}</span></div>
                      <div className={rowCls}><span className="text-neutral-500">Cost</span><span className={numCls} data-testid="rally-per-client-cost">{fmtIsk0(perClientCost)}</span></div>
                      <div className={rowCls}><span className="text-neutral-500">Margin</span><span className={numCls} data-testid="rally-per-client-margin">{fmtIsk0(margin)}</span></div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="border border-neutral-800/80 bg-black/20 p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">At {booked} booked</p>
                    <div className="mt-2 divide-y divide-neutral-800/60">
                      <div className={rowCls}><span className="text-neutral-500">Total income</span><span className={numCls} data-testid="rally-total-income">{fmtIsk0(totalIncome)}</span></div>
                      <div className={rowCls}><span className="text-neutral-500">Total cost</span><span className={numCls} data-testid="rally-total-cost">{fmtIsk0(totalCost)}</span></div>
                      <div className={rowCls}><span className="text-emerald-300/80">Total commission</span><span className={numCls} data-testid="rally-total-commission">{fmtIsk0(totalComm)}</span></div>
                    </div>
                  </div>
                  <div className="border border-amber-500/25 bg-amber-500/[0.06] p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Breakeven</p>
                    <p data-testid="rally-breakeven" className="mt-2 font-luxe text-2xl tracking-[0.08em] text-amber-200">
                      {breakeven === null ? "Never — no margin" : `${breakeven} client${breakeven === 1 ? "" : "s"}`}
                    </p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-neutral-600">cover the shared staff cost</p>
                  </div>
                  <div className="border border-emerald-500/25 bg-emerald-500/[0.05] p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Sold out {maxC > 0 ? `(${maxC} clients)` : ""}</p>
                    <p data-testid="rally-soldout-profit" className="mt-2 font-luxe text-2xl tracking-[0.08em] text-emerald-300">
                      {soldOutProfit === null ? "Set max capacity" : fmtIsk0(soldOutProfit)}
                    </p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-neutral-600">profit at full capacity</p>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="border border-amber-500/15 bg-[#14141a] p-6">
          <div className="flex items-center justify-between">
            <p className={labelCls}>Itinerary — day by day: activities, lunches, dinners & private events</p>
          </div>
          <datalist id="roster-driver-names">
            {roster.map((d) => (
              <option key={d.id} value={d.name} />
            ))}
          </datalist>
          <div className="mt-4 space-y-5">
            {dayGroups.map((g) => (
              <div key={g.day} data-testid={`day-section-${g.day}`} className="border border-neutral-800/70 bg-black/10">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-amber-500/40 bg-amber-500/[0.08] px-4 py-3">
                  <p data-testid={`day-label-${g.day}`} className="font-luxe text-sm uppercase tracking-[0.25em] text-amber-200">
                    Day {g.day}
                    {arrival ? <span className="ml-2 text-[10px] tracking-[0.3em] text-amber-300/70">— {new Date(`${addDaysIso(arrival, g.day - 1)}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</span> : ""}
                  </p>
                  <button
                    data-testid={`day-add-${g.day}`}
                    onClick={() => setItems((it) => [...it, newItem(g.day)])}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <Plus className="h-3 w-3" /> Add to Day {g.day}
                  </button>
                </div>
                <div className="space-y-4 p-4">
                  {g.rows.length === 0 && (
                    <p data-testid={`day-empty-${g.day}`} className="border border-dashed border-neutral-800 px-4 py-4 text-center text-[11px] text-neutral-600">
                      Nothing yet — add to Day {g.day}
                    </p>
                  )}
                  {g.rows.map(({ item, idx: i }) => {
              const si = stayIdxForItem(item); // >= 0 → this line mirrors that stay's money (stored once, on the stay)
              return (
              <div
                key={i}
                data-testid={`trip-item-row-${i}`}
                draggable
                onDragStart={() => {
                  dragRowRef.current = i;
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragRowRef.current !== null && dragRowRef.current !== i) moveRow(dragRowRef.current, i);
                  dragRowRef.current = null;
                }}
                className="border border-neutral-800 bg-[#0d0d12] p-4"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span title="Drag to reorder — or use the little arrows">
                    <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-neutral-500 transition-colors hover:text-amber-300" />
                  </span>
                  <span className="flex shrink-0 flex-col">
                    <button
                      data-testid={`trip-item-up-${i}`}
                      disabled={i === 0}
                      onClick={() => moveRow(i, i - 1)}
                      className="text-neutral-600 transition-colors hover:text-amber-300 disabled:opacity-20"
                      aria-label="Move up"
                      title="Move up"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`trip-item-down-${i}`}
                      disabled={i === items.length - 1}
                      onClick={() => moveRow(i, i + 1)}
                      className="text-neutral-600 transition-colors hover:text-amber-300 disabled:opacity-20"
                      aria-label="Move down"
                      title="Move down"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </span>
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    Day
                    <input
                      data-testid={`trip-item-day-${i}`}
                      type="number"
                      min="1"
                      max="60"
                      value={item.day}
                      onChange={(e) => {
                        const d = Number(e.target.value) || 1;
                        if (d === item.day) return;
                        setItem(i, "day", d);
                        // jump straight to the day this moment moved to
                        setTimeout(() => document.querySelector(`[data-testid="day-section-${d}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
                      }}
                      className="w-16 border border-amber-900/40 bg-[#0d0d12] px-2 py-2 text-sm text-amber-100 outline-none focus:border-amber-400"
                    />
                  </label>
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-500" title="Last day of a multi-day booking (rental car etc.) — empty = single day">
                    Until
                    <input
                      data-testid={`trip-item-endday-${i}`}
                      type="number"
                      min={item.day}
                      max="60"
                      placeholder="—"
                      value={item.end_day || ""}
                      onChange={(e) => setItem(i, "end_day", Number(e.target.value) || 0)}
                      className="w-16 border border-amber-900/40 bg-[#0d0d12] px-2 py-2 text-sm text-amber-100 outline-none focus:border-amber-400"
                    />
                  </label>
                  <input data-testid={`trip-item-time-${i}`} placeholder="19:30" value={item.time} onChange={(e) => setItem(i, "time", e.target.value)} className={`${inputCls} !w-24`} />
                  <input
                    data-testid={`trip-item-price-${i}`}
                    placeholder="Price"
                    title={`${si >= 0 ? "Same stay as in Hotels & Stays — edit here or there, it is stored once and counted once" : "Client-facing price — type math like 25.000*4 and it calculates when you leave the field"}${exprHint(si >= 0 ? hotels[si] : item, "price")}`}
                    value={si >= 0 ? hotelPriceForTotal(hotels[si]) || "" : item.price || ""}
                    onChange={(e) => (si >= 0 ? setHotel(si, "price", e.target.value) : setItem(i, "price", e.target.value))}
                    onBlur={() => (si >= 0 ? calcField(hotels[si], setHotel, si, "price") : calcField(item, setItem, i, "price"))}
                    className={`${inputCls} !w-28`}
                  />
                  {si < 0 && (
                    <input
                      data-testid={`trip-item-perperson-${i}`}
                      placeholder="per pers."
                      title={paxN ? `Per-person rate — the price box gets this × ${paxN} guests (and re-prices if pax changes)` : "Per-person rate — set guests (pax) above and the price box fills itself"}
                      value={item.per_person || ""}
                      onChange={(e) => setPerPerson(i, e.target.value)}
                      className={`${inputCls} !w-24 border-dashed border-amber-500/40`}
                    />
                  )}
                  {si < 0 && (
                    <label className="flex items-center gap-1.5" title={item.end_day > item.day ? "Divide the total across the booked days — the return day is free (24h rule)" : "Set an 'Until' day first — then the total divides across the booked days (return day free)"}>
                      <input
                        data-testid={`trip-item-split-${i}`}
                        type="checkbox"
                        disabled={!(item.end_day > item.day)}
                        checked={!!item.split_price}
                        onChange={(e) => setItem(i, "split_price", e.target.checked)}
                        className="h-3.5 w-3.5 accent-amber-500 disabled:opacity-30"
                      />
                      <span className={`text-[9px] uppercase tracking-[0.18em] ${item.end_day > item.day ? "text-neutral-500" : "text-neutral-700"}`}>
                        ÷ days{item.end_day > item.day ? "" : " (set Until first)"}
                      </span>
                    </label>
                  )}
                  <input
                    data-testid={`trip-item-cost-${i}`}
                    placeholder="Cost"
                    title={`${si >= 0 ? "Same stay as in Hotels & Stays — edit here or there, it is stored once" : "Your cost — private, never shown to clients — math works here too"}${exprHint(si >= 0 ? hotels[si] : item, "cost")}${costSplitHint(si >= 0 ? hotels[si] : item)}`}
                    value={si >= 0 ? hotels[si].cost || "" : item.cost || ""}
                    onChange={(e) => (si >= 0 ? setHotel(si, "cost", e.target.value) : setItem(i, "cost", e.target.value))}
                    onBlur={() => (si >= 0 ? calcField(hotels[si], setHotel, si, "cost") : calcField(item, setItem, i, "cost"))}
                    className={`${inputCls} !w-24 border-dashed`}
                  />
                  <CostLinesControl
                    row={si >= 0 ? hotels[si] : item}
                    testid={`trip-item-costlines-${i}`}
                    onApply={(lines, totalStr) => {
                      const applyTo = si >= 0 ? setHotel : setItem;
                      const idx = si >= 0 ? si : i;
                      applyTo(idx, "cost_lines", lines);
                      if (totalStr) {
                        applyTo(idx, "cost", totalStr);
                        autoMoney({ ...(si >= 0 ? hotels[si] : item), cost: totalStr }, applyTo, idx);
                      }
                    }}
                  />
                  <input
                    data-testid={`trip-item-commission-${i}`}
                    placeholder="Comm."
                    title={`${si >= 0 ? "Same stay as in Hotels & Stays — auto-fills from price − cost" : "Your commission — auto-fills from price − cost, or type 20% to take that share of the price"}${exprHint(si >= 0 ? hotels[si] : item, "commission")}`}
                    value={si >= 0 ? hotels[si].commission || "" : item.commission || ""}
                    onChange={(e) => (si >= 0 ? setHotel(si, "commission", e.target.value) : setItem(i, "commission", e.target.value))}
                    onBlur={() => (si >= 0 ? calcField(hotels[si], setHotel, si, "commission") : calcField(item, setItem, i, "commission"))}
                    className={`${inputCls} !w-24 border-dashed`}
                  />
                  <select
                    data-testid={`trip-item-type-${i}`}
                    value={item.type}
                    onChange={(e) => setItem(i, "type", e.target.value)}
                    className="border border-amber-900/40 bg-[#0d0d12] px-2 py-2.5 text-sm text-amber-100 outline-none focus:border-amber-400"
                  >
                    {ITEM_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <input data-testid={`trip-item-title-${i}`} placeholder="Title — e.g. Private ice cave dinner" value={item.title} onChange={(e) => setItem(i, "title", e.target.value)} className={`${inputCls} min-w-48 flex-1`} />
                  <span className="relative flex items-center">
                    <User className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-neutral-600" />
                    <input
                      data-testid={`trip-item-assignee-${i}`}
                      list="roster-driver-names"
                      placeholder="Driver / guide"
                      title="Who does this job — pick from the roster or type any name. Assigned jobs land on the calendar and on their live list; the client sees their roster contact"
                      value={item.assignee || ""}
                      onChange={(e) => setItem(i, "assignee", e.target.value)}
                      className={`${inputCls} !w-36 !pl-8`}
                    />
                  </span>
                  {item.type === "hotel" && si < 0 && hotels.some((h) => h.name?.trim() && h.hotel_id) && (
                    <select
                      data-testid={`trip-item-linkstay-${i}`}
                      value=""
                      title="This hotel line isn't linked to a stay yet — link it and it shows the stay's price, cost and commission (stored once, counted once)"
                      onChange={(e) => {
                        const h = hotels[parseInt(e.target.value, 10)];
                        if (h?.hotel_id) {
                          setItems((xs) => xs.map((x, xi) => (xi === i ? { ...x, stay_id: h.hotel_id, price: "", cost: "", commission: "" } : x)));
                          toast.success(`Linked to ${h.name} — the stay now owns the money on this line`);
                        }
                      }}
                      className="border border-amber-500/40 bg-[#0d0d12] px-2 py-2.5 text-[10px] uppercase tracking-[0.15em] text-amber-200/80 outline-none focus:border-amber-400"
                    >
                      <option value="">Link to a stay…</option>
                      {hotels.map((h, hi) => (h.name?.trim() && h.hotel_id ? <option key={hi} value={hi}>{h.name}</option> : null))}
                    </select>
                  )}
                  {token && trip?.id && item.item_id && (
                    <>
                      <label className="flex items-center gap-1.5" title="I do this job myself — no vendor invoice needed, no VAT credit">
                        <input
                          data-testid={`trip-item-selfdone-${i}`}
                          type="checkbox"
                          checked={!!item.self_done}
                          onChange={(e) => setItem(i, "self_done", e.target.checked)}
                          className="h-3.5 w-3.5 accent-amber-500"
                        />
                        <span className="text-[9px] uppercase tracking-[0.18em] text-neutral-500">Myself</span>
                      </label>
                      <label className="flex items-center gap-1.5" title="There is no invoice for this one — stop reminding me about it">
                        <input
                          data-testid={`trip-item-noinvoice-${i}`}
                          type="checkbox"
                          checked={!!item.no_invoice}
                          onChange={(e) => setItem(i, "no_invoice", e.target.checked)}
                          className="h-3.5 w-3.5 accent-amber-500"
                        />
                        <span className="text-[9px] uppercase tracking-[0.18em] text-neutral-500">No inv.</span>
                      </label>
                      <VendorInvoiceControl
                        base={`${API}/owner/trips/${trip.id}`}
                        headers={headers}
                        kind="item"
                        rowKey={item.item_id}
                        row={item}
                        onField={(f, v) => setItem(i, f, f === "vendor_vat" ? Number(v) : v)}
                        onRows={(items2) => {
                          const src = (items2 || []).find((x) => x.item_id === item.item_id);
                          if (src)
                            setItems((xs) =>
                              xs.map((x, xi) =>
                                xi === i
                                  ? { ...x, vendor_invoice_path: src.vendor_invoice_path || "", vendor_invoice_type: src.vendor_invoice_type || "", vendor_invoice_at: src.vendor_invoice_at || "" }
                                  : x
                              )
                            );
                        }}
                        testid={`trip-item-inv-${i}`}
                      />
                    </>
                  )}
                  <button
                    data-testid={`trip-item-insert-${i}`}
                    onClick={() => setItems((it) => [...it.slice(0, i + 1), newItem(item.day), ...it.slice(i + 1)])}
                    title="Insert a blank moment right below this one (same day)"
                    className="text-neutral-600 transition-colors hover:text-amber-300"
                    aria-label="Insert moment below"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <button data-testid={`trip-item-remove-${i}`} onClick={() => setItems((it) => it.filter((_, idx) => idx !== i))} className="text-neutral-600 transition-colors hover:text-red-400" aria-label="Remove moment">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <input
                  data-testid={`trip-item-details-${i}`}
                  placeholder="Details — inclusions, pickup, dress code, surprises..."
                  value={item.details}
                  onChange={(e) => setItem(i, "details", e.target.value)}
                  className={`${inputCls} mt-3`}
                />
                {item.type === "transport" && (
                  <div data-testid={`trip-item-transfer-${i}`} className="mt-2 flex flex-wrap items-center gap-2 border-l-2 border-amber-500/25 py-1 pl-4">
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-amber-400/60" />
                    <input
                      data-testid={`trip-item-pickuploc-${i}`}
                      placeholder="Pickup at — e.g. KEF arrivals hall"
                      value={item.pickup_loc || ""}
                      onChange={(e) => setItem(i, "pickup_loc", e.target.value)}
                      className={`${inputCls} !w-52`}
                    />
                    <span className="text-xs text-neutral-600">→</span>
                    <input
                      data-testid={`trip-item-dropoffloc-${i}`}
                      placeholder="Drop-off at — e.g. The EDITION"
                      value={item.dropoff_loc || ""}
                      onChange={(e) => setItem(i, "dropoff_loc", e.target.value)}
                      className={`${inputCls} !w-52`}
                    />
                    <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-500" title="When the client wants to be back at the hotel">
                      Back by
                      <input
                        data-testid={`trip-item-endtime-${i}`}
                        type="time"
                        value={item.end_time || ""}
                        onChange={(e) => setItem(i, "end_time", e.target.value)}
                        className={`${inputCls} !w-24`}
                      />
                    </label>
                  </div>
                )}
                <textarea
                  data-testid={`trip-item-notes-${i}`}
                  rows={1}
                  placeholder="Private operator notes — names, confirmations, references (never shown to client)"
                  value={item.operator_notes || ""}
                  onChange={(e) => setItem(i, "operator_notes", e.target.value)}
                  className={`${inputCls} mt-2 resize-none border-dashed text-neutral-400`}
                />
                {item.client_action && (
                  <p className={`mt-2 text-[10px] uppercase tracking-[0.2em] ${item.client_action === "removed" ? "text-red-400/90" : "text-amber-300/90"}`}>
                    {item.client_action === "removed" ? "Client took this out" : `Alternative requested${item.client_note ? ` — ${item.client_note}` : ""}`}
                  </p>
                )}
                {(item.type === "dinner" || item.type === "lunch") && (
                  <div className="mt-3 border-l border-amber-500/15 pl-4">
                    <p className="text-[9px] uppercase tracking-[0.2em] text-neutral-600">
                      Restaurant options — the client taps one on their itinerary (max 3)
                    </p>
                    {favRestaurants.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-600">Favourites:</span>
                        {favRestaurants.map((f, fi) => {
                          const used = (item.options || []).some((o) => o.name?.trim().toLowerCase() === f.name.toLowerCase());
                          const full = (item.options || []).length >= 3;
                          return (
                            <span key={f.name} className="flex items-center border border-neutral-700">
                              <button
                                type="button"
                                data-testid={`fav-add-${i}-${fi}`}
                                disabled={used || full}
                                onClick={() =>
                                  setItem(i, "options", [
                                    ...(item.options || []),
                                    { option_id: crypto.randomUUID().replace(/-/g, "").slice(0, 8), name: f.name, link: f.link || "" },
                                  ])
                                }
                                title={used ? "Already in the options" : full ? "Max 3 options" : `Add ${f.name} as an option`}
                                className="px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] text-amber-200/90 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-amber-200/90"
                              >
                                {f.name}
                              </button>
                              <button
                                type="button"
                                data-testid={`fav-remove-${i}-${fi}`}
                                onClick={() => removeFavourite(f.name)}
                                className="px-1.5 text-neutral-600 transition-colors hover:text-red-400"
                                aria-label={`Remove ${f.name} from favourites`}
                                title="Remove from favourites"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="mt-2 space-y-1.5">
                      {(item.options || []).map((o, oi) => (
                        <div key={o.option_id || oi} className="flex flex-wrap items-center gap-2">
                          <input
                            data-testid={`menu-option-name-${i}-${oi}`}
                            placeholder="Restaurant — e.g. Dill"
                            value={o.name}
                            onChange={(e) => setItem(i, "options", (item.options || []).map((x, xi) => (xi === oi ? { ...x, name: e.target.value } : x)))}
                            className={`${inputCls} min-w-32 flex-1`}
                          />
                          <input
                            data-testid={`menu-option-link-${i}-${oi}`}
                            placeholder="Menu link — https://…"
                            value={o.link || ""}
                            onChange={(e) => setItem(i, "options", (item.options || []).map((x, xi) => (xi === oi ? { ...x, link: e.target.value } : x)))}
                            className={`${inputCls} min-w-40 flex-1`}
                          />
                          <button
                            data-testid={`menu-option-fav-${i}-${oi}`}
                            onClick={() => saveFavourite(o)}
                            disabled={!token}
                            className="text-neutral-600 transition-colors hover:text-amber-300 disabled:opacity-30"
                            aria-label="Save to favourite restaurants"
                            title="Save to your favourite restaurants — one tap to reuse on any trip"
                          >
                            <Star className="h-4 w-4" />
                          </button>
                          <button
                            data-testid={`menu-option-remove-${i}-${oi}`}
                            onClick={() => setItem(i, "options", (item.options || []).filter((_, xi) => xi !== oi))}
                            className="text-neutral-600 transition-colors hover:text-red-400"
                            aria-label="Remove option"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    {(item.options || []).length < 3 && (
                      <button
                        data-testid={`menu-option-add-${i}`}
                        onClick={() =>
                          setItem(i, "options", [
                            ...(item.options || []),
                            { option_id: crypto.randomUUID().replace(/-/g, "").slice(0, 8), name: "", link: "" },
                          ])
                        }
                        className="mt-2 flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                      >
                        <Plus className="h-3 w-3" /> Add restaurant option
                      </button>
                    )}
                    {item.picked_option && (item.options || []).some((o) => o.option_id === item.picked_option) && (
                      <p data-testid={`menu-picked-note-${i}`} className="mt-2 text-[10px] uppercase tracking-[0.2em] text-emerald-300/80">
                        Client chose: {(item.options || []).find((o) => o.option_id === item.picked_option)?.name}
                      </p>
                    )}
                  </div>
                )}
              </div>
              );
                  })}
                </div>
              </div>
            ))}
            <div className="pt-1 text-center">
              <button
                data-testid="trip-add-day"
                onClick={() => setItems((it) => [...it, newItem(dayGroups.length + 1)])}
                className="inline-flex items-center gap-1.5 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
              >
                <Plus className="h-3 w-3" /> Add day
              </button>
            </div>
          </div>
        </div>
      </div>

      {(() => {
        // ISK is the working currency — EUR/USD entries convert at the display rates; plain numbers count as ISK
        // stay-linked lines are excluded (mirrored on the stay); the rental counts once — its total
        const all = [...hotels, ...moneyItems];
        const totalPrice = sumIsk([...all.map((x) => x.price), ...rentals.map((r) => r.price)]);
        const totalCost = sumIsk([...all, ...rentals].map((x) => x.cost));
        const totalCommission = sumIsk([...all, ...rentals].map((x) => x.commission));
        if (!totalPrice && !totalCost && !totalCommission) return null;
        const approx = (n) => (clientRegion === "eu" ? fmtEurApprox(n / ISK_PER_EUR) : fmtUsdApprox(n / ISK_PER_USD));
        const approxCls = "ml-2 text-xs font-normal normal-case tracking-normal text-neutral-500";
        return (
          <div data-testid="trip-money-panel" className="mt-8 border border-amber-500/25 bg-amber-500/5 p-6">
            <p className={labelCls}>The Money — private, never shown to clients</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div className="border border-amber-500/15 bg-[#0c0c10] p-4 text-center">
                <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Full price — client pays</p>
                <p data-testid="trip-total-price" className="mt-2 font-luxe text-2xl tracking-wide text-amber-100">
                  {fmtIsk(totalPrice)}
                  <span className={approxCls}>{approx(totalPrice)}</span>
                </p>
              </div>
              <div className="border border-amber-500/15 bg-[#0c0c10] p-4 text-center">
                <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Your cost</p>
                <p data-testid="trip-total-cost" className="mt-2 font-luxe text-2xl tracking-wide text-neutral-200">
                  {fmtIsk(totalCost)}
                  <span className={approxCls}>{approx(totalCost)}</span>
                </p>
              </div>
              <div className="border border-amber-400/40 bg-amber-500/10 p-4 text-center">
                <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300/80">Your commission</p>
                <p data-testid="trip-total-commission" className="mt-2 font-luxe text-2xl tracking-wide text-amber-200">
                  {fmtIsk(totalCommission)}
                  <span className={approxCls}>{approx(totalCommission)}</span>
                </p>
              </div>
            </div>
            <button
              data-testid="open-costs-page"
              onClick={() => setCostsOpen(true)}
              className="mt-4 w-full border border-amber-500/40 px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
            >
              Full cost page — every price, cost & commission on one screen
            </button>
          </div>
        );
      })()}

      {trip?.id && (
        <div data-testid="trim-budget-panel" className="mt-4 border border-neutral-800 bg-[#0c0c10] p-6">
          <p className={labelCls}>Trim to a budget — private</p>
          <p className="mt-2 text-xs leading-relaxed text-neutral-500">
            Client says the price is too high? Enter their budget and get concrete suggestions on what to take out
            or swap for a cheaper version.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input
              data-testid="trim-budget-input"
              placeholder="Client budget, e.g. 4.500.000 ISK"
              value={trimBudget}
              onChange={(e) => setTrimBudget(e.target.value)}
              className={`${inputCls} sm:w-72`}
            />
            <button
              data-testid="trim-budget-button"
              onClick={runBudgetTrim}
              disabled={trimBusy}
              className="border border-amber-500/60 px-6 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-50"
            >
              {trimBusy ? "Thinking…" : "Get savings plan"}
            </button>
          </div>
          {trimResult && (
            <div data-testid="trim-results" className="mt-5 border-t border-neutral-800 pt-5">
              {trimResult.over_isk <= 0 ? (
                <p data-testid="trim-within-budget" className="text-sm text-emerald-300">
                  Already within budget — the trip totals {fmtIsk(trimResult.total_isk)} against {fmtIsk(trimResult.budget_isk)}.
                </p>
              ) : (
                <>
                  <p className="text-xs uppercase tracking-[0.25em]">
                    <span className="text-neutral-400">Total {fmtIsk(trimResult.total_isk)} · Budget {fmtIsk(trimResult.budget_isk)} · </span>
                    <span data-testid="trim-over" className="font-semibold text-red-400">Over by {fmtIsk(trimResult.over_isk)}</span>
                  </p>
                  {trimResult.headline && <p className="mt-3 font-serif text-lg font-light text-neutral-200">{trimResult.headline}</p>}
                  <div className="mt-4 space-y-3">
                    {trimResult.suggestions.map((s, si) => (
                      <div key={si} data-testid={`trim-suggestion-${si}`} className="border border-neutral-800 bg-black/30 p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.2em] ${
                            s.action === "remove" ? "border-red-500/50 text-red-300" : "border-amber-500/50 text-amber-300"
                          }`}>
                            {s.action}
                          </span>
                          <span className="text-sm text-neutral-100">{s.target}{!s.matched && <span className="ml-1 text-[9px] uppercase tracking-[0.15em] text-neutral-600">(general)</span>}</span>
                          {s.est_saving_isk > 0 && (
                            <span data-testid={`trim-saving-${si}`} className="ml-auto text-xs font-semibold text-emerald-300">−{fmtIsk(s.est_saving_isk)}</span>
                          )}
                        </div>
                        <p className="mt-2 text-sm text-neutral-300">{s.suggestion}</p>
                        {s.why && <p className="mt-1 text-xs text-neutral-500">{s.why}</p>}
                      </div>
                    ))}
                  </div>
                  {(() => {
                    // suggestions can overlap (two ideas for the same line) — the sum is an upper bound
                    const savings = trimResult.suggestions.reduce((acc, s) => acc + (s.est_saving_isk || 0), 0);
                    const newTotal = Math.max(0, trimResult.total_isk - savings);
                    const fits = newTotal <= trimResult.budget_isk;
                    return (
                      <p data-testid="trim-summary" className={`mt-4 text-xs uppercase tracking-[0.25em] ${fits ? "text-emerald-300" : "text-amber-300"}`}>
                        Together these save up to ≈ {fmtIsk(savings)} → new total as low as ≈ {fmtIsk(newTotal)}
                        {fits ? " — within budget" : ` — still ${fmtIsk(newTotal - trimResult.budget_isk)} over`}
                      </p>
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {costsOpen && (() => {
        const staff = rallyStaffCosts();
        const staffOn = !!rally.include_in_total;
        const costInclStaff = totalCostIsk + (staffOn ? staff.staffTotal : 0);
        // what still floats after vendor costs (+ staff when counted) and the commission boxes
        const unassigned = totalPriceIsk - costInclStaff - totalCommIsk;
        const pricePerCouple = moneyToIsk(rally.price_override) || totalPriceIsk; // what one booking pays
        const marginPerClient = pricePerCouple - totalCostIsk; // reference margin per couple — NOT the commission
        const maxClients = parseInt(capacity, 10) || 0;
        const breakeven = marginPerClient > 0 && staff.staffTotal > 0 ? Math.max(1, Math.ceil(staff.staffTotal / marginPerClient)) : null;
        const soldOutProfit = maxClients > 0 ? marginPerClient * maxClients - staff.staffTotal : null;
        // the owner's walk-through: margin per couple × couples booked − staff = where the trip stands
        const bookedCouples = parseInt(rally.clients_booked, 10) || 0;
        const groupResult = marginPerClient * bookedCouples - staff.staffTotal;
        const namedStays = hotels.map((h, i) => ({ h, i })).filter(({ h }) => h.name.trim());
        // a line adds up when price = cost + commission (only checked once at least two boxes are filled)
        const lineAddsUp = (p, c, m) => {
          const filled = (p > 0 ? 1 : 0) + (c > 0 ? 1 : 0) + (m > 0 ? 1 : 0);
          return filled < 2 || p === c + m;
        };
        const mismatchChip = (p, c, m, tid) =>
          lineAddsUp(p, c, m) ? null : (
            <span data-testid={tid} title="This line doesn't add up — price should equal cost + commission. It stays yellow until it's fixed." className="shrink-0 border border-yellow-400/80 bg-yellow-500/15 px-1 py-px text-[8px] font-semibold uppercase tracking-[0.12em] text-yellow-300">
              {fmtIsk(p)} ≠ {fmtIsk(c)} + {fmtIsk(m)}
            </span>
          );
        const unsplitChip = (p, c, m, tid) =>
          p > 0 && c === 0 && m === 0 ? (
            <span data-testid={tid} title="Price is filled but cost and commission are empty — they count as 0 until you split it" className="shrink-0 border border-amber-500/50 bg-amber-500/10 px-1 py-px text-[8px] font-semibold uppercase tracking-[0.12em] text-amber-300">
              no cost/comm yet
            </span>
          ) : null;
        const countedLines = [
          ...namedStays.map(({ h }) => ({ name: h.name, p: moneyToIsk(hotelPriceForTotal(h)) || 0, c: moneyToIsk(h.cost) || 0, m: moneyToIsk(h.commission) || 0 })),
          ...items.filter((it) => it.title?.trim() && stayIdxForItem(it) < 0 && !it.ops_full && !it.ops_client_books && !it.ops_person && !it.ops_info).map((it) => ({ name: it.title, p: moneyToIsk(it.price) || 0, c: moneyToIsk(it.cost) || 0, m: moneyToIsk(it.commission) || 0 })),
          ...rentals.filter((r) => r.name.trim()).map((r) => ({ name: r.name, p: moneyToIsk(r.price) || 0, c: moneyToIsk(r.cost) || 0, m: moneyToIsk(r.commission) || 0 })),
        ];
        const mismatchLines = countedLines.filter(({ p, c, m }) => !lineAddsUp(p, c, m));
        const mismatchCount = mismatchLines.length;
        // price filled but cost and commission left empty — the "missing" piece of the totals
        const unsplitLines = countedLines.filter(({ p, c, m }) => p > 0 && c === 0 && m === 0);
        const unsplitTotal = unsplitLines.reduce((s, l) => s + l.p, 0);
        const moneyIn = (value, onChange, onBlur, tid, dashed) => (
          <input
            data-testid={tid}
            value={value || ""}
            onChange={onChange}
            inputMode="decimal"
            onBlur={() => {
              onBlur?.();
              const fmt = blurFmtVal(value);
              if (fmt !== (value || "")) onChange({ target: { value: fmt } });
            }}
            placeholder="0"
            className={`${inputCls} !w-full !px-2 !py-1.5 !text-xs ${dashed ? "border-dashed" : ""}`}
          />
        );
        const rowGrid = "grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_5.5rem] items-center gap-1.5 border border-neutral-800/70 bg-black/30 px-2 py-1";
        const SectionHead = ({ title, sub, testid }) => (
          <div className="mt-5 border-b border-neutral-800 pb-1.5">
            <p data-testid={testid} className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">{title}</p>
            {sub && <p className="mt-0.5 text-[10px] text-neutral-600">{sub}</p>}
          </div>
        );
        return (
          <div data-testid="costs-page" className="fixed inset-0 z-50 overflow-y-auto bg-black/85 p-4 backdrop-blur-sm" onClick={() => setCostsOpen(false)}>
            <div className="mx-auto my-8 w-full max-w-4xl border border-amber-500/25 bg-[#0c0c10] p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">The Cost Page — private</p>
                  <h3 className="mt-2 font-serif text-2xl font-light text-neutral-50 sm:text-3xl">Every price, cost & commission</h3>
                  <p className="mt-2 text-xs text-neutral-500">
                    Edit any number here — it updates the same place as the stays, the itinerary and the rentals. Stored once, counted once. Save the trip when you're done.
                  </p>
                </div>
                <button data-testid="costs-close" onClick={() => setCostsOpen(false)} aria-label="Close" className="p-1 text-neutral-500 transition-colors hover:text-amber-200">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-6 grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_5.5rem] gap-1.5 px-2 text-[8px] font-semibold uppercase tracking-[0.2em] text-neutral-600">
                <span>Line</span>
                <span>Price (client)</span>
                <span>Cost (vendor)</span>
                <span>Commission</span>
              </div>

              <SectionHead title="Stays" testid="costs-section-stays" />
              {namedStays.length === 0 && <p className="mt-2 text-xs text-neutral-600">No stays yet.</p>}
              <div className="mt-2 space-y-1">
                {namedStays.map(({ h, i }) => {
                  const bp = moneyToIsk(hotelPriceForTotal(h)) || 0, bc = moneyToIsk(h.cost) || 0, bm = moneyToIsk(h.commission) || 0;
                  const bad = !lineAddsUp(bp, bc, bm);
                  return (
                  <div key={h.hotel_id || i} data-testid={`costs-stay-${i}`} className={`${rowGrid} ${bad ? "!border-yellow-400/80 !bg-yellow-500/5" : ""}`}>
                    <span className="flex flex-wrap items-baseline gap-2 font-serif text-[13px] text-neutral-100">
                      <span className="truncate">{h.name}</span>
                      {h.nights ? <span className="shrink-0 text-[9px] uppercase tracking-[0.15em] text-neutral-600">{h.nights} n</span> : null}
                      {mismatchChip(bp, bc, bm, `costs-stay-mismatch-${i}`)}
                      {unsplitChip(bp, bc, bm, `costs-stay-unsplit-${i}`)}
                    </span>
                    {moneyIn(h.price, (e) => setHotel(i, "price", e.target.value), () => calcField(hotels[i], setHotel, i, "price"), `costs-stay-price-${i}`)}
                    {moneyIn(h.cost, (e) => setHotel(i, "cost", e.target.value), () => calcField(hotels[i], setHotel, i, "cost"), `costs-stay-cost-${i}`, true)}
                    {moneyIn(h.commission, (e) => setHotel(i, "commission", e.target.value), () => calcField(hotels[i], setHotel, i, "commission"), `costs-stay-comm-${i}`, true)}
                  </div>
                  );
                })}
              </div>

              <SectionHead title="Itinerary" sub="Lines tagged ↔ stay mirror that stay's money — edit either place." testid="costs-section-items" />
              <div className="mt-2 space-y-1">
                {items.map((item, i) => {
                  if (!item.title?.trim()) return null;
                  const si = stayIdxForItem(item);
                  const countedOut = item.ops_full || item.ops_client_books || item.ops_person || item.ops_info;
                  const bp = si >= 0 ? moneyToIsk(hotelPriceForTotal(hotels[si])) || 0 : moneyToIsk(item.price) || 0;
                  const bc = si >= 0 ? moneyToIsk(hotels[si].cost) || 0 : moneyToIsk(item.cost) || 0;
                  const bm = si >= 0 ? moneyToIsk(hotels[si].commission) || 0 : moneyToIsk(item.commission) || 0;
                  const bad = !item.ops_info && !lineAddsUp(bp, bc, bm);
                  return (
                    <div key={item.item_id || i} data-testid={`costs-item-${i}`} className={`grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_5.5rem_auto] items-center gap-1.5 border px-2 py-1 ${countedOut ? "border-neutral-800/50 opacity-60" : "border-neutral-800/70 bg-black/30"} ${bad ? "!border-yellow-400/80 !bg-yellow-500/5 !opacity-100" : ""}`}>
                      <span className="flex flex-wrap items-baseline gap-1.5 font-serif text-[13px] text-neutral-100">
                        <span className="shrink-0 text-[9px] uppercase tracking-[0.15em] text-neutral-600">D{item.day}</span>
                        <span className="truncate">{item.title}</span>
                        {si >= 0 && <span className="shrink-0 border border-amber-500/40 px-1 py-px text-[8px] uppercase tracking-[0.12em] text-amber-300/80">↔ stay</span>}
                        {countedOut && <span className="shrink-0 border border-neutral-700 px-1 py-px text-[8px] uppercase tracking-[0.12em] text-neutral-500">not counted</span>}
                        {!item.ops_info && mismatchChip(bp, bc, bm, `costs-item-mismatch-${i}`)}
                        {!item.ops_info && unsplitChip(bp, bc, bm, `costs-item-unsplit-${i}`)}
                      </span>
                      {item.ops_info ? (
                        <span className="col-span-3 text-center text-[8px] uppercase tracking-[0.15em] text-neutral-600">no price — info only</span>
                      ) : (
                        <>
                          {moneyIn(
                            si >= 0 ? hotelPriceForTotal(hotels[si]) : item.price,
                            (e) => (si >= 0 ? setHotel(si, "price", e.target.value) : setItem(i, "price", e.target.value)),
                            () => (si >= 0 ? calcField(hotels[si], setHotel, si, "price") : calcField(items[i], setItem, i, "price")),
                            `costs-item-price-${i}`
                          )}
                          {moneyIn(
                            si >= 0 ? hotels[si].cost : item.cost,
                            (e) => (si >= 0 ? setHotel(si, "cost", e.target.value) : setItem(i, "cost", e.target.value)),
                            () => (si >= 0 ? calcField(hotels[si], setHotel, si, "cost") : calcField(items[i], setItem, i, "cost")),
                            `costs-item-cost-${i}`,
                            true
                          )}
                          {moneyIn(
                            si >= 0 ? hotels[si].commission : item.commission,
                            (e) => (si >= 0 ? setHotel(si, "commission", e.target.value) : setItem(i, "commission", e.target.value)),
                            () => (si >= 0 ? calcField(hotels[si], setHotel, si, "commission") : calcField(items[i], setItem, i, "commission")),
                            `costs-item-comm-${i}`,
                            true
                          )}
                        </>
                      )}
                      <button
                        data-testid={`costs-item-noprice-${i}`}
                        onClick={() => setItem(i, "ops_info", !item.ops_info)}
                        title={item.ops_info ? "Info-only line — press to give it a price again" : "No price for this line (e.g. info) — removes the money boxes and keeps it out of every total"}
                        className={`shrink-0 border px-2 py-1 text-[8px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                          item.ops_info
                            ? "border-neutral-600 bg-neutral-800/60 text-neutral-300"
                            : "border-neutral-700 text-neutral-500 hover:border-amber-400 hover:text-amber-200"
                        }`}
                      >
                        {item.ops_info ? "no price ✓" : "no price"}
                      </button>
                    </div>
                  );
                })}
              </div>

              {rentals.filter((r) => r.name.trim()).length > 0 && (
                <>
                  <SectionHead title="Rentals" sub="Total per vehicle — not per day." testid="costs-section-rentals" />
                  <div className="mt-2 space-y-1">
                    {rentals.map((r, i) => {
                      if (!r.name.trim()) return null;
                      const bp = moneyToIsk(r.price) || 0, bc = moneyToIsk(r.cost) || 0, bm = moneyToIsk(r.commission) || 0;
                      const bad = !lineAddsUp(bp, bc, bm);
                      return (
                        <div key={r.rental_id || i} data-testid={`costs-rental-${i}`} className={`${rowGrid} ${bad ? "!border-yellow-400/80 !bg-yellow-500/5" : ""}`}>
                          <span className="flex flex-wrap items-baseline gap-2 font-serif text-[13px] text-neutral-100">
                            <span className="truncate">{r.name}</span>
                            {mismatchChip(bp, bc, bm, `costs-rental-mismatch-${i}`)}
                            {unsplitChip(bp, bc, bm, `costs-rental-unsplit-${i}`)}
                          </span>
                          {moneyIn(r.price, (e) => setRental(i, "price", e.target.value), null, `costs-rental-price-${i}`)}
                          {moneyIn(r.cost, (e) => setRental(i, "cost", e.target.value), null, `costs-rental-cost-${i}`, true)}
                          {moneyIn(r.commission, (e) => setRental(i, "commission", e.target.value), null, `costs-rental-comm-${i}`, true)}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}


              <p data-testid="costs-formula" className="mt-6 border border-amber-500/20 bg-black/40 px-3 py-2 text-center text-[10px] uppercase tracking-[0.2em] leading-relaxed text-neutral-400">
                <span className="text-amber-100">{fmtIsk0(totalPriceIsk)}</span> client pays = <span className="text-neutral-200">{fmtIsk0(totalCostIsk)}</span> your cost
                {staffOn && staff.staffTotal > 0 ? <> + <span className="text-neutral-200">{fmtIsk0(staff.staffTotal)}</span> staff</> : null} + <span className="text-emerald-300">{fmtIsk0(totalCommIsk)}</span> commission + <span className="text-amber-300">{fmtIsk0(unassigned)}</span> not split yet.
                Commission = the commission boxes you filled in (empty boxes count as 0).
              </p>
              {mismatchCount > 0 && (
                <div data-testid="costs-mismatch-banner" className="mt-2 border border-yellow-400/60 bg-yellow-500/10 px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.2em] leading-relaxed text-yellow-300">
                  {mismatchCount} line{mismatchCount === 1 ? " doesn't" : "s don't"} add up — price must equal cost + commission — fix: {mismatchLines.map((l) => l.name).join(", ")}
                </div>
              )}
              {unsplitTotal > 0 && (
                <div data-testid="costs-unsplit-banner" className="mt-2 border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-center text-[10px] uppercase tracking-[0.2em] leading-relaxed text-amber-200/90">
                  {unsplitLines.length} line{unsplitLines.length === 1 ? " has" : "s have"} a price but no cost &amp; commission yet ({fmtIsk(unsplitTotal)} of price) — fill them on: {unsplitLines.map((l) => l.name).join(", ")}
                </div>
              )}
              <div data-testid="costs-totals" className="sticky bottom-0 mt-3 grid gap-2 border-t border-amber-500/20 bg-[#0c0c10] pb-1 pt-4 sm:grid-cols-4">
                <div className="border border-amber-500/15 bg-black/30 px-3 py-2 text-center">
                  <p className="text-[9px] uppercase tracking-[0.25em] text-neutral-500">Client pays</p>
                  <p data-testid="costs-total-price" className="mt-0.5 font-luxe text-lg tracking-wide text-amber-100">{fmtIsk0(totalPriceIsk)}</p>
                </div>
                <div className="border border-amber-500/15 bg-black/30 px-3 py-2 text-center">
                  <p className="text-[9px] uppercase tracking-[0.25em] text-neutral-500">Your cost{staffOn ? " (incl. staff)" : ""}</p>
                  <p data-testid="costs-total-cost" className="mt-0.5 font-luxe text-lg tracking-wide text-neutral-200">{fmtIsk0(costInclStaff)}</p>
                  {staff.staffTotal > 0 && !staffOn && (
                    <p data-testid="costs-staff-excluded-note" className="text-[8px] uppercase tracking-[0.15em] text-neutral-600">staff {fmtIsk0(staff.staffTotal)} not included</p>
                  )}
                </div>
                <div className="border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-center">
                  <p className="text-[9px] uppercase tracking-[0.25em] text-amber-300/80">Your commission — boxes only</p>
                  <p data-testid="costs-total-commission" className="mt-0.5 font-luxe text-lg tracking-wide text-amber-200">{fmtIsk0(totalCommIsk)}</p>
                </div>
                <div
                  className="border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-center"
                  title="The part of the client's payment not assigned to a vendor cost or a commission box yet. It sits on the amber-tagged lines above — fill in their cost & commission and this goes to 0. When staff is counted in your cost, staff is paid out of this too."
                >
                  <p className="text-[9px] uppercase tracking-[0.25em] text-amber-300/80">Not split yet</p>
                  <p data-testid="costs-total-unsplit" className="mt-0.5 font-luxe text-lg tracking-wide text-amber-100">{fmtIsk0(unassigned)}</p>
                  {unsplitTotal > 0 ? (
                    staffOn && staff.staffTotal > 0 ? (
                      <p data-testid="costs-unsplit-note" className="text-[8px] uppercase tracking-[0.15em] text-neutral-600">the amber lines hold {fmtIsk0(unsplitTotal)} — staff {fmtIsk0(staff.staffTotal)} comes out of it</p>
                    ) : (
                      <p data-testid="costs-unsplit-note" className="text-[8px] uppercase tracking-[0.15em] text-neutral-600">sitting on the amber lines above — fill in their cost &amp; commission</p>
                    )
                  ) : (
                    <p data-testid="costs-unsplit-note" className="text-[8px] uppercase tracking-[0.15em] text-neutral-600">every priced line is split into cost + commission</p>
                  )}
                </div>
              </div>

              {/* staff + group math — only for rallies/public tours, or when the owner adds staff */}
              {staffVisible ? (
              <div data-testid="costs-rally" className="mt-4 border border-amber-500/25 bg-amber-500/5 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Staff &amp; the group math</p>
                  {!isPublicTour && !rally.enabled && (
                    <button
                      data-testid="costs-staff-remove"
                      onClick={() => setRally((r) => ({ ...r, staff_added: false, include_in_total: false }))}
                      title="Take staff costs off this trip — nothing is lost, they come back if you add staff again"
                      className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 transition-colors hover:text-red-400"
                    >
                      ✕ Remove staff
                    </button>
                  )}
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <label className="block">
                    <span className="text-[8px] uppercase tracking-[0.2em] text-neutral-500">Staff coming along</span>
                    <input
                      data-testid="costs-staff-count"
                      type="number"
                      min="0"
                      value={rally.staff_count}
                      onChange={(e) => setRally((r) => ({ ...r, staff_count: e.target.value }))}
                      placeholder="0"
                      className={`${inputCls} mt-1 !py-1.5 !text-xs`}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[8px] uppercase tracking-[0.2em] text-neutral-500">Couples booked</span>
                    <input
                      data-testid="costs-clients-booked"
                      type="number"
                      min="0"
                      value={rally.clients_booked}
                      onChange={(e) => setRally((r) => ({ ...r, clients_booked: e.target.value }))}
                      placeholder="0"
                      className={`${inputCls} mt-1 !py-1.5 !text-xs`}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[8px] uppercase tracking-[0.2em] text-neutral-500">Price per couple — raise it here to test</span>
                    <input
                      data-testid="costs-price-per-couple"
                      value={rally.price_override || ""}
                      onChange={(e) => setRally((r) => ({ ...r, price_override: e.target.value }))}
                      onBlur={() => setRally((r) => ({ ...r, price_override: blurFmtVal(rally.price_override) }))}
                      placeholder={`empty = trip total (${fmtIsk(totalPriceIsk)})`}
                      className={`${inputCls} mt-1 !py-1.5 !text-xs`}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[8px] uppercase tracking-[0.2em] text-neutral-500">Max clients on the trip</span>
                    <input
                      data-testid="costs-max-clients"
                      type="number"
                      min="0"
                      value={capacity}
                      onChange={(e) => setCapacity(e.target.value)}
                      placeholder="e.g. 10"
                      className={`${inputCls} mt-1 !py-1.5 !text-xs`}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[8px] uppercase tracking-[0.2em] text-neutral-500">Staff car — whole trip</span>
                    <input
                      data-testid="costs-staff-car-input"
                      value={rally.staff_car || ""}
                      onChange={(e) => setRally((r) => ({ ...r, staff_car: e.target.value }))}
                      onBlur={() => { const v = evalMoneyExpr(rally.staff_car); setRally((r) => ({ ...r, staff_car: v || blurFmtVal(rally.staff_car) })); }}
                      placeholder="total ISK"
                      title="One staff vehicle for the whole trip — total ISK, math works here"
                      className={`${inputCls} mt-1 !py-1.5 !text-xs`}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[8px] uppercase tracking-[0.2em] text-neutral-500">Staff lunch — per day (auto from the trip's lunches)</span>
                    <input
                      data-testid="costs-staff-lunch-input"
                      value={rally.staff_lunch_rate || ""}
                      onChange={(e) => setRally((r) => ({ ...r, staff_lunch_rate: e.target.value }))}
                      onBlur={() => { const v = evalMoneyExpr(rally.staff_lunch_rate); setRally((r) => ({ ...r, staff_lunch_rate: v || blurFmtVal(rally.staff_lunch_rate) })); }}
                      placeholder="per staff / day"
                      title="Pre-filled from the average client lunch per person — change it anytime"
                      className={`${inputCls} mt-1 !py-1.5 !text-xs`}
                    />
                  </label>
                </div>
                <div className="mt-3 space-y-1 text-xs">
                  <div className="flex items-center justify-between border border-neutral-800/70 bg-black/30 px-2 py-1.5">
                    <span className="text-neutral-400">Staff hotels ({staff.staffN} × {staff.awayNights} away nights)</span>
                    <span className="font-semibold text-neutral-200">{fmtIsk0(staff.staffHotel)}</span>
                  </div>
                  <div className="flex items-center justify-between border border-neutral-800/70 bg-black/30 px-2 py-1.5">
                    <span className="text-neutral-400">Staff dinners ({staff.staffN} × {staff.totalNights} nights, incl. Reykjavík)</span>
                    <span className="font-semibold text-neutral-200">{fmtIsk0(staff.staffDinner)}</span>
                  </div>
                  <div className="flex items-center justify-between border border-neutral-800/70 bg-black/30 px-2 py-1.5">
                    <span className="text-neutral-400">Staff lunches ({staff.staffN} × {staff.tripDays} days, incl. Reykjavík)</span>
                    <span className="font-semibold text-neutral-200" data-testid="costs-staff-lunch">{fmtIsk0(staff.staffLunch)}</span>
                  </div>
                  <div className="flex items-center justify-between border border-neutral-800/70 bg-black/30 px-2 py-1.5">
                    <span className="text-neutral-400">Blue Lagoon ({staff.staffN} staff)</span>
                    <span className="font-semibold text-neutral-200">{fmtIsk0(staff.staffActivity)}</span>
                  </div>
                  <div data-testid="costs-staff-car" className="flex items-center justify-between border border-neutral-800/70 bg-black/30 px-2 py-1.5">
                    <span className="text-neutral-400">Staff car (whole trip){staff.staffCar > 0 ? "" : " — enter it above"}</span>
                    <span className="font-semibold text-neutral-200">{fmtIsk0(staff.staffCar)}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 border border-amber-500/30 bg-black/30 px-2 py-1.5">
                    <span data-testid="costs-staff-total" className="font-semibold text-amber-200">Staff cost shared: {fmtIsk0(staff.staffTotal)}</span>
                    <label className="flex items-center gap-2 text-[9px] uppercase tracking-[0.2em] text-neutral-400">
                      <input
                        data-testid="costs-staff-include"
                        type="checkbox"
                        checked={staffOn}
                        onChange={(e) => setRally((r) => ({ ...r, include_in_total: e.target.checked }))}
                        className="h-3.5 w-3.5 accent-amber-500"
                      />
                      Count staff in the trip's cost above
                    </label>
                  </div>
                </div>
                <div className={`mt-3 border px-3 py-2.5 text-center ${groupResult < 0 ? "border-red-500/40 bg-red-500/5" : "border-emerald-500/40 bg-emerald-500/5"}`}>
                  <p className="text-[8px] uppercase tracking-[0.2em] text-neutral-500">Right now — {bookedCouples} couple{bookedCouples === 1 ? "" : "s"} at {fmtIsk0(pricePerCouple)}</p>
                  <p data-testid="costs-rally-result" className={`mt-1 font-luxe text-base ${groupResult < 0 ? "text-red-300" : "text-emerald-300"}`}>
                    {groupResult < 0 ? `${fmtIsk0(-groupResult)} in minus` : groupResult > 0 ? `${fmtIsk0(groupResult)} profit` : "Break even"}
                  </p>
                  <p className="text-[8px] text-neutral-600">margin {fmtIsk0(marginPerClient)} × {bookedCouples} couple{bookedCouples === 1 ? "" : "s"} − staff {fmtIsk0(staff.staffTotal)}</p>
                </div>
                <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                  <div className="border border-neutral-800 bg-black/30 px-3 py-2.5 text-center">
                    <p className="text-[8px] uppercase tracking-[0.2em] text-neutral-500">Couples to break even</p>
                    <p data-testid="costs-rally-breakeven" className="mt-1 font-luxe text-base text-amber-100">
                      {breakeven === null ? (marginPerClient <= 0 ? "No margin per couple" : "No staff cost") : `${breakeven} couple${breakeven === 1 ? "" : "s"}`}
                    </p>
                    <p className="text-[8px] text-neutral-600">margin {fmtIsk0(marginPerClient)} per couple (price − costs)</p>
                  </div>
                  <div className="border border-neutral-800 bg-black/30 px-3 py-2.5 text-center">
                    <p className="text-[8px] uppercase tracking-[0.2em] text-neutral-500">Profit if sold out{maxClients ? ` (${maxClients})` : ""}</p>
                    <p data-testid="costs-rally-soldout" className={`mt-1 font-luxe text-base ${soldOutProfit !== null && soldOutProfit > 0 ? "text-emerald-300" : "text-neutral-200"}`}>
                      {soldOutProfit === null ? "Set max clients above" : fmtIsk0(soldOutProfit)}
                    </p>
                    <p className="text-[8px] text-neutral-600">{maxClients ? `${maxClients} × ${fmtIsk0(marginPerClient)} − staff ${fmtIsk0(staff.staffTotal)}` : "how many can join at most"}</p>
                  </div>
                </div>
              </div>
              ) : (
                <button
                  data-testid="costs-staff-add"
                  onClick={() => setRally((r) => ({ ...r, staff_added: true }))}
                  className="mt-4 w-full border border-dashed border-amber-500/30 px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-neutral-500 transition-colors hover:border-amber-400 hover:text-amber-200"
                >
                  + Add staff to this trip — hotel, meals, one shared car
                </button>
              )}
              <button
                data-testid="costs-done"
                onClick={() => setCostsOpen(false)}
                className="mt-6 w-full border border-amber-500/60 px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
              >
                Done — back to the trip
              </button>
            </div>
          </div>
        );
      })()}

      {!adminToken && (
        <div data-testid="public-tour-section" className="mt-8 border border-amber-500/15 bg-[#14141a] p-6">
          <p className={labelCls}>Public Tour — sell tickets, anyone can sign up</p>
          <p className="mt-2 text-xs leading-relaxed text-neutral-500">
            Turn this itinerary into a ticketed tour. You get a public page to advertise — people sign up with their
            own arrival and departure times, and each one appears in Guests below with their own itinerary link.
          </p>
          <label className="mt-4 flex items-center gap-3">
            <input
              data-testid="public-tour-toggle"
              type="checkbox"
              checked={isPublicTour}
              onChange={(e) => setIsPublicTour(e.target.checked)}
              className="h-4 w-4 accent-amber-500"
            />
            <span className="text-sm text-amber-100">Open public signups for this trip</span>
          </label>
          {isPublicTour && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label>
                <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Ticket price (per person)</span>
                <input data-testid="public-tour-price" placeholder="e.g. 2.400 EUR" value={ticketPrice} onChange={(e) => setTicketPrice(e.target.value)} className={`${inputCls} mt-1.5`} />
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Capacity (0 = unlimited)</span>
                <input data-testid="public-tour-capacity" type="number" min="0" placeholder="e.g. 20" value={capacity} onChange={(e) => setCapacity(e.target.value)} className={`${inputCls} mt-1.5`} />
              </label>
              <label className="flex items-center gap-3 self-end pb-2 sm:col-span-2">
                <input
                  data-testid="public-tour-hide-spots"
                  type="checkbox"
                  checked={hideSpots}
                  onChange={(e) => setHideSpots(e.target.checked)}
                  className="h-4 w-4 accent-amber-500"
                />
                <span className="text-sm text-amber-100">Don't show how many places are left</span>
              </label>
              <label className="sm:col-span-2">
                <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">Cover image URL (optional)</span>
                <input data-testid="public-tour-cover" placeholder="e.g. /assets/tours/below-zero.jpg" value={coverImage} onChange={(e) => setCoverImage(e.target.value)} className={`${inputCls} mt-1.5`} />
              </label>
              {coverImage && (
                <div className="sm:col-span-2">
                  <img data-testid="public-tour-cover-preview" src={coverImage} alt="Tour cover preview" className="h-40 w-full border border-amber-500/20 object-cover" />
                  <p className="mt-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-600">Cover preview — shows at the top of the public tour page</p>
                </div>
              )}
            </div>
          )}
          {isPublicTour && trip?.public_token && (
            <div className="mt-4 flex items-center gap-2">
              <input
                readOnly
                data-testid="public-tour-link"
                value={`${window.location.origin}/tour/${trip.public_token}`}
                onFocus={(e) => e.target.select()}
                className={`${inputCls} flex-1`}
              />
              <button
                data-testid="public-tour-copy"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/tour/${trip.public_token}`);
                  toast.success("Public tour link copied — advertise it anywhere");
                }}
                className="shrink-0 border border-amber-500/50 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
              >
                Copy Link
              </button>
            </div>
          )}
          {isPublicTour && !trip?.public_token && (
            <p className="mt-3 text-xs text-amber-300/70">Save the itinerary once to generate the public signup link.</p>
          )}
        </div>
      )}

      {trip?.id && !adminToken && (
        <div data-testid="guests-section" className="mt-8 border border-amber-500/15 bg-[#14141a] p-6">
          <p className={labelCls}>Guests & Access — for big groups</p>
          <p className="mt-2 text-xs leading-relaxed text-neutral-500">
            Send each guest their private link. They sign up with name, email and how many they bring — then always
            have access to the itinerary and costs through the same link.
          </p>
          <div className="mt-4 space-y-2">
            {guests.length === 0 && <p className="text-sm text-neutral-600">No guest links yet.</p>}
            {guests.map((g, gi) => (
              <div key={g.guest_token} data-testid={`guest-row-${gi}`} className="flex flex-wrap items-center gap-2 border border-neutral-800 bg-[#0d0d12] px-3 py-2.5">
                <span className="text-xs text-neutral-300">
                  {g.registered ? (
                    <>
                      <span className="text-amber-200">{g.name}</span> · {g.email} · party of {g.pax}
                    </>
                  ) : (
                    <span className="text-neutral-500">Not joined yet{g.email ? ` · ${g.email}` : ""}</span>
                  )}
                </span>
                {g.registered && (() => {
                  const n = Math.min(2, Math.max(1, parseInt(g.pax, 10) || 1));
                  const dinners = n === 1 ? dinnerTotalIsk / 2 : dinnerTotalIsk;
                  const bill = ticketIsk * n + dinners;
                  if (!dinnerTotalIsk && !ticketIsk) return null;
                  return (
                    <span data-testid={`guest-bill-${gi}`} className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                      {dinnerTotalIsk > 0 && (
                        <>
                          · dinners {fmtIsk(dinners)}
                          {n === 1 && <span className="text-emerald-300/80"> (½ — solo)</span>}
                        </>
                      )}
                      {ticketIsk > 0 && (
                        <> · to bill ≈ <span className="text-amber-200">{fmtIsk(bill)}</span></>
                      )}
                    </span>
                  );
                })()}
                <div className="ml-auto flex items-center gap-2">
                  <button
                    data-testid={`guest-copy-${gi}`}
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/trip/${trip.client_token}/g/${g.guest_token}`);
                      toast.success("Guest link copied");
                    }}
                    className="border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    Copy Link
                  </button>
                  <button
                    data-testid={`guest-remove-${gi}`}
                    onClick={async () => {
                      try {
                        await axios.delete(`${API}/owner/trips/${trip.id}/guests/${g.guest_token}`, { headers });
                        setGuests((gs) => gs.filter((x) => x.guest_token !== g.guest_token));
                        toast.success("Guest link removed");
                      } catch {
                        toast.error("Could not remove the guest.");
                      }
                    }}
                    className="text-neutral-600 transition-colors hover:text-red-400"
                    aria-label="Remove guest"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            data-testid="add-guest-button"
            onClick={async () => {
              try {
                const res = await axios.post(`${API}/owner/trips/${trip.id}/guests`, {}, { headers });
                setGuests((gs) => [...gs, { guest_token: res.data.guest_token, registered: false }]);
                navigator.clipboard.writeText(res.data.link);
                toast.success("Guest link created and copied — send it to them");
              } catch {
                toast.error("Could not create a guest link.");
              }
            }}
            className="mt-4 flex items-center gap-2 border border-amber-500/50 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
          >
            <Plus className="h-3.5 w-3.5" /> Add Guest Link
          </button>

          <div className="mt-6 border-t border-amber-500/10 pt-5">
            <p className={labelCls}>Trip Admin — access to this trip only</p>
            <p className="mt-2 text-xs leading-relaxed text-neutral-500">
              Invite a partner or guide: they get a link that opens only this trip&apos;s editor, never your full desk.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                data-testid="trip-admin-email-input"
                type="email"
                placeholder="Their email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className={`${inputCls} min-w-56 flex-1`}
              />
              <button
                data-testid="invite-admin-button"
                onClick={async () => {
                  if (!adminEmail.trim()) {
                    toast.error("Add their email first.");
                    return;
                  }
                  try {
                    const res = await axios.post(`${API}/owner/trips/${trip.id}/admins`, { email: adminEmail }, { headers });
                    setAdmins((as) => [...as, { token: res.data.token, email: adminEmail }]);
                    navigator.clipboard.writeText(res.data.link);
                    toast.success(res.data.email_id ? "Invitation emailed — link also copied" : "Link copied — send it to them");
                    setAdminEmail("");
                  } catch (e) {
                    const detail = e.response?.data?.detail;
                    toast.error(typeof detail === "string" ? detail : "Could not invite the admin.");
                  }
                }}
                className="border border-amber-500/50 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
              >
                Invite Trip Admin
              </button>
            </div>
            {admins.length > 0 && (
              <div className="mt-3 space-y-2">
                {admins.map((a, ai) => (
                  <div key={a.token} data-testid={`admin-row-${ai}`} className="flex flex-wrap items-center gap-2 text-xs text-neutral-400">
                    <span className="text-amber-200/90">{a.email}</span>
                    <button
                      data-testid={`admin-copy-${ai}`}
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/trip-admin/${a.token}`);
                        toast.success("Admin link copied");
                      }}
                      className="border border-neutral-700 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-neutral-400 transition-colors hover:border-amber-400 hover:text-amber-200"
                    >
                      Copy Link
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {snapshot && (
        <div data-testid="undo-banner" className="mt-8 flex flex-wrap items-center justify-between gap-3 border border-amber-400/50 bg-amber-500/10 px-5 py-4">
          <p className="text-xs uppercase tracking-[0.2em] text-amber-200">
            AI revision applied — not what you wanted?
          </p>
          <button
            data-testid="undo-revision-button"
            onClick={() => {
              setHotels(snapshot.hotels);
              setItems(snapshot.items);
              setSnapshot(null);
              toast.success("Rolled back to before the revision");
            }}
            className="border border-amber-400 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
          >
            Undo Revision
          </button>
        </div>
      )}

      {(totalPriceIsk > 0 || totalCostIsk > 0) && (
        <div data-testid="trip-money-totals" className="mt-8 flex flex-wrap gap-x-8 gap-y-3 border border-amber-500/20 bg-[#0a0a0d] px-5 py-4">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-neutral-500">Trip total — client</p>
            <p data-testid="totals-price" className="mt-1 font-luxe text-lg tracking-wide text-amber-100">
              {fmtIsk(totalPriceIsk)} <span className="text-xs font-normal normal-case tracking-normal text-neutral-500">{clientRegion === "eu" ? fmtEurApprox(totalPriceIsk / ISK_PER_EUR) : fmtUsdApprox(totalPriceIsk / ISK_PER_USD)}</span>
            </p>
          </div>
          {totalCostIsk > 0 && (
            <div>
              <p className="text-[9px] uppercase tracking-[0.25em] text-neutral-500">Your cost — private</p>
              <p data-testid="totals-cost" className="mt-1 font-luxe text-lg tracking-wide text-neutral-300">{fmtIsk(totalCostIsk)}</p>
            </div>
          )}
          {totalCommIsk > 0 && (
            <div>
              <p className="text-[9px] uppercase tracking-[0.25em] text-neutral-500">Your commission — private · commission boxes only</p>
              <p data-testid="totals-commission" className="mt-1 font-luxe text-lg tracking-wide text-emerald-300">{fmtIsk(totalCommIsk)}</p>
            </div>
          )}
        </div>
      )}

      <div className="sticky bottom-0 mt-8 flex flex-wrap items-center gap-3 border-t border-amber-500/15 bg-[#0c0c10]/95 py-4 backdrop-blur-xl">
        <button
          data-testid="trip-save-button"
          onClick={() => save()}
          disabled={saving}
          className="flex items-center gap-2 bg-amber-500 px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {trip?.id ? "Save Changes" : "Save Itinerary"}
        </button>
        {trip?.id && !adminToken && !["pending_confirmation", "sent_to_operator", "confirmed"].includes(status) && (
          <button
            data-testid="trip-approve-button"
            onClick={approveMyself}
            disabled={approving}
            title="Approve this itinerary yourself — driving jobs go straight to the calendar awaiting assignment"
            className="flex items-center gap-2 border border-emerald-500/50 px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-emerald-300 transition-colors hover:bg-emerald-500 hover:text-black disabled:opacity-60"
          >
            {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Approve myself
          </button>
        )}
        {trip?.id && status === "pending_confirmation" && (
          <span data-testid="trip-approved-note" className="flex items-center gap-2 px-2 text-[10px] uppercase tracking-[0.2em] text-emerald-300/90">
            <Check className="h-4 w-4" /> Approved — on the calendar
          </span>
        )}
        <button
          data-testid="trip-preview-button"
          onClick={openPreview}
          className="flex items-center gap-2 border border-amber-500/40 px-6 py-3 text-xs uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
        >
          <Eye className="h-4 w-4" /> Preview Demo
        </button>
        <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-600">
          Nothing is confirmed until you mark it confirmed
        </p>
      </div>

      <AnimatePresence>
        {previewOpen && (
          <motion.div
            data-testid="trip-preview-modal"
            className="fixed inset-0 z-[95] overflow-y-auto bg-black/85 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="mx-auto max-w-3xl px-5 py-10">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border border-amber-500/40 bg-amber-500/10 px-5 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-200">
                  Demo — exactly what your client will see · drag to rearrange, bin to remove, click a title or time to edit — permanent once you Save Changes
                </p>
                <button data-testid="preview-close-button" onClick={() => setPreviewOpen(false)} className="text-amber-200 transition-colors hover:text-white" aria-label="Close preview">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="border border-amber-500/20 bg-[#070709] p-6 sm:p-10">
                <TripView
                  trip={{
                    client,
                    arrival_date: arrival,
                    departure_date: departure,
                    arrival_time: arrivalTime,
                    departure_time: departureTime,
                    pax,
                    // mirror the client exactly: fully-booked rows leave the itinerary and the total
                    hotels: hotels.filter((h) => h.name.trim() && !h.ops_full),
                    items: items.filter((i) => i.title.trim() && !i.ops_full),
                  }}
                  onMove={moveItem}
                  showGapWarnings
                  onDelete={(id) => {
                    setItems((it) => it.filter((x) => x.item_id !== id));
                    toast.success("Moment removed — press Save Changes to make it permanent");
                  }}
                  onEdit={(id, field, value) => {
                    setItems((it) => it.map((x) => (x.item_id === id ? { ...x, [field]: value } : x)));
                    toast.success("Updated — press Save Changes to make it permanent");
                  }}
                  showClientMarks
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
